/* eslint-disable @typescript-eslint/naming-convention, no-alert */
import React, {
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Routes,
  Route,
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Button,
  Table,
  Stack,
  Spinner,
  Alert,
  Form,
  Modal,
  Pagination,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  Dropdown,
  Offcanvas,
  Tabs,
  Tab,
} from 'react-bootstrap';
import {
  FaTrash,
  FaEdit,
  FaEllipsisV,
  FaLinkedin,
  FaFilePdf,
  FaPlusCircle,
} from 'react-icons/fa';
import {SiIndeed} from 'react-icons/si';
import {LuFileJson} from 'react-icons/lu';
import {VscSparkleFilled} from 'react-icons/vsc';
import {RxDragHandleDots2} from 'react-icons/rx';
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
  FloatingFocusManager,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
} from '@floating-ui/react';
import ResumeForm, {validateResumePayload} from '../models/resume-form';
import {resumeThemes, resumeThemesNew} from '../models/other';
import type {
  ResumeDbModel,
  ResumeListInfo,
  JobDescriptionDbModel,
  JobDescriptionListInfo,
} from '../models/database-models';
import type {ResumeSchema} from '../models/resume';
import {BasicAiAlert} from '../libs/alert';
import {useAppDispatch} from '../store/hooks';
import {
  setHasTailored,
  setHasResume,
  setHasExported,
} from '../store/quickstart-slice';
import type {ResumeCritique} from '../models/resume-critique';
import ImportPdfPage, {ImportFromPlatformsModal} from './import-pdf';

const previewDebounceMs = 100;

type ResumeWorkerMessage =
  | {type: 'success'; svg: string; mainContent: string}
  | {type: 'pdf_success'; pdfData: Uint8Array}
  | {type: 'error'; message: string};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringOption(
  value: unknown,
  options: readonly string[],
): value is string {
  return typeof value === 'string' && options.includes(value);
}

function isResumeSchema(value: unknown): value is ResumeSchema {
  return isRecord(value);
}

function isResumeDbModel(value: unknown): value is ResumeDbModel {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.user_id === 'string' &&
    typeof value.name === 'string' &&
    isResumeSchema(value.resume)
  );
}

export function useResumeTheme(modelId: string | undefined) {
  const [selectedTheme, setSelectedTheme] = useState<string>(() => {
    try {
      const key = `resume_theme_${modelId ?? 'global'}`;
      const saved = localStorage.getItem(key);
      if (isStringOption(saved, resumeThemesNew)) {
        return saved;
      }
    } catch {
      /* ignore */
    }

    return 'straightforward';
  });

  const hasLoadedThemeRef = useRef<boolean>(true);

  // Sync theme with local storage.
  // IMPORTANT: only re-sync when the model id changes.
  useEffect(() => {
    hasLoadedThemeRef.current = false;

    try {
      const key = `resume_theme_${modelId ?? 'global'}`;
      const saved = localStorage.getItem(key);
      if (isStringOption(saved, resumeThemesNew)) {
        setSelectedTheme(saved);
      } else {
        setSelectedTheme('straightforward');
      }
    } catch {
      /* ignore */
    } finally {
      hasLoadedThemeRef.current = true;
    }
  }, [modelId]);

  useEffect(() => {
    if (!hasLoadedThemeRef.current) {
      return;
    }

    try {
      const key = `resume_theme_${modelId ?? 'global'}`;
      localStorage.setItem(key, selectedTheme);
    } catch {
      /* ignore */
    }
  }, [selectedTheme, modelId]);

  return [selectedTheme, setSelectedTheme] as const;
}

function isUnknownArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function formatFastApiDetail(data: unknown): string | undefined {
  if (!isRecord(data)) {
    return undefined;
  }

  const {detail} = data as {detail?: unknown};
  if (typeof detail === 'string') {
    return detail;
  }

  if (!Array.isArray(detail)) {
    return undefined;
  }

  const messages: string[] = [];
  for (const item of detail) {
    if (typeof item === 'string') {
      messages.push(item);
      continue;
    }

    if (!isRecord(item)) {
      continue;
    }

    const message = typeof item.msg === 'string' ? item.msg : undefined;
    if (!message) {
      continue;
    }

    if (Array.isArray(item.loc)) {
      const loc = item.loc
        .map(part =>
          typeof part === 'string' || typeof part === 'number'
            ? String(part)
            : '',
        )
        .filter(Boolean)
        .join('.');
      messages.push(loc ? `${loc}: ${message}` : message);
      continue;
    }

    messages.push(message);
  }

  if (messages.length > 0) {
    return messages.join('; ');
  }

  return undefined;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data: unknown = error.response?.data;
    const detailMessage = formatFastApiDetail(data);
    if (detailMessage) {
      return detailMessage;
    }

    if (typeof data === 'string') {
      return data;
    }

    if (isRecord(data)) {
      const {message} = data as {message?: unknown};
      if (typeof message === 'string') {
        return message;
      }
    }

    return error.message || fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
}

/**
 * Simple left/right resizable split component.
 *
 * Usage:
 * <ResizableSplit initialLeftPercent={50}>
 *   <div>left content</div>
 *   <div>right content</div>
 * </ResizableSplit>
 *
 * This uses a draggable vertical divider and supports mouse + touch.
 */
function ResizableSplit({
  children,
  initialLeftPercent = 50,
}: {
  children: React.ReactNode;
  initialLeftPercent?: number;
}): React.JSX.Element {
  const array = React.Children.toArray(children);
  const leftNode = array[0] ?? null;
  const rightNode = array[1] ?? null;

  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const startxRef = useRef(0);
  const startLeftRef = useRef(0);

  const [leftPercent, setLeftPercent] = useState<number>(initialLeftPercent);

  useEffect(() => {
    const onMove = (clientX: number) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();
      const total = rect.width;
      const delta = clientX - startxRef.current;
      const newLeftPx = (startLeftRef.current / 100) * total + delta;
      let percent = (newLeftPx / total) * 100;
      percent = Math.max(10, Math.min(90, percent)); // Clamp
      setLeftPercent(Math.round(percent));
    };

    const onMouseMove = (event: MouseEvent) => {
      if (!draggingRef.current) {
        return;
      }

      onMove(event.clientX);
    };

    const onMouseUp = () => {
      draggingRef.current = false;
      document.body.style.userSelect = '';
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!draggingRef.current) {
        return;
      }

      onMove(event.touches[0].clientX);
    };

    const onTouchEnd = () => {
      draggingRef.current = false;
      document.body.style.userSelect = '';
    };

    globalThis.addEventListener('mousemove', onMouseMove);
    globalThis.addEventListener('mouseup', onMouseUp);
    globalThis.addEventListener('touchmove', onTouchMove, {passive: false});
    globalThis.addEventListener('touchend', onTouchEnd);

    return () => {
      globalThis.removeEventListener('mousemove', onMouseMove);
      globalThis.removeEventListener('mouseup', onMouseUp);
      globalThis.removeEventListener('touchmove', onTouchMove);
      globalThis.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  const onDividerMouseDown = (clientX: number) => {
    draggingRef.current = true;
    startxRef.current = clientX;
    startLeftRef.current = leftPercent;
    document.body.style.userSelect = 'none';
  };

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        width: '100%',
        alignItems: 'stretch',
        gap: 8,
      }}
    >
      <div
        style={{
          width: `${leftPercent}%`,
          minWidth: 160,
          overflow: 'auto',
        }}
      >
        {leftNode}
      </div>

      <div
        role='separator'
        aria-orientation='vertical'
        aria-label='Resize panels'
        onMouseDown={event => {
          onDividerMouseDown(event.clientX);
        }}
        onTouchStart={event => {
          onDividerMouseDown(event.touches[0].clientX);
        }}
        style={{
          width: 16,
          cursor: 'col-resize',
          position: 'relative',
          flex: '0 0 auto',
          borderLeft: '2px solid #dee2e6',
          backgroundClip: 'padding-box',
          background: '#eff0f3',
        }}
      >
        <RxDragHandleDots2
          size={16}
          color='#6c757d'
          aria-hidden
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            opacity: 0.9,
          }}
        />
      </div>

      <div
        style={{
          flex: 1,
          overflow: 'auto',
        }}
      >
        {rightNode}
      </div>
    </div>
  );
}

function MobileRowMenu({
  onEdit,
  onViewExport,
  onExport,
  onExportJson,
  onCritique,
  onDelete,
  disabled,
}: {
  onEdit?: () => void;
  onViewExport?: () => void;
  onExport?: () => void;
  onExportJson?: () => void;
  onCritique?: () => void;
  onDelete?: () => void;
  disabled?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const {refs, floatingStyles, context} = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), shift({padding: 8})],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, {role: 'menu'});
  const {getReferenceProps, getFloatingProps} = useInteractions([
    click,
    dismiss,
    role,
  ]);

  return (
    <div className='d-flex d-md-none justify-content-end'>
      <button
        type='button'
        className='btn btn-secondary btn-sm'
        ref={refs.setReference}
        {...getReferenceProps()}
        disabled={disabled}
        aria-haspopup='menu'
        aria-expanded={open}
      >
        <FaEllipsisV />
      </button>

      {open ? (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                zIndex: 2000,
                display: 'inline-block',
                width: 'auto',
                minWidth: 'auto',
                maxWidth: '80vw',
                overflow: 'hidden',
              }}
              className='dropdown-menu show'
              {...getFloatingProps()}
            >
              {onEdit ? (
                <button
                  className='dropdown-item icon-btn flex-nowrap'
                  onClick={() => {
                    onEdit();
                    setOpen(false);
                  }}
                >
                  <FaEdit size={18} />
                  Edit
                </button>
              ) : null}
              {onViewExport ? (
                <button
                  className='dropdown-item d-inline-flex align-items-center flex-nowrap'
                  onClick={() => {
                    onViewExport();
                    setOpen(false);
                  }}
                >
                  <FaFilePdf size={18} className='me-2' />
                  View/Export PDF
                </button>
              ) : null}
              {onExport ? (
                <button
                  className='dropdown-item d-inline-flex align-items-center flex-nowrap'
                  onClick={() => {
                    onExport();
                    setOpen(false);
                  }}
                >
                  <FaFilePdf size={18} className='me-2' />
                  Export PDF (Legacy)
                </button>
              ) : null}
              {onExportJson ? (
                <button
                  className='dropdown-item icon-btn flex-nowrap'
                  onClick={() => {
                    onExportJson();
                    setOpen(false);
                  }}
                >
                  <LuFileJson size={16} />
                  Export JSON
                </button>
              ) : null}
              {onCritique ? (
                <button
                  className='dropdown-item icon-btn flex-nowrap'
                  onClick={() => {
                    onCritique();
                    setOpen(false);
                  }}
                >
                  <VscSparkleFilled size={16} className='typst-svg-override' />
                  AI Critique
                </button>
              ) : null}
              {onDelete ? (
                <>
                  <div className='dropdown-divider' />
                  <button
                    className='dropdown-item text-danger icon-btn flex-nowrap'
                    onClick={() => {
                      onDelete();
                      setOpen(false);
                    }}
                  >
                    <FaTrash size={18} />
                    Delete
                  </button>
                </>
              ) : null}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </div>
  );
}

function ResumesList(): React.JSX.Element {
  const [items, setItems] = React.useState<ResumeListInfo[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [exportingId, setExportingId] = React.useState<string | undefined>(
    undefined,
  );
  const [exportingJsonId, setExportingJsonId] = React.useState<
    string | undefined
  >(undefined);
  const [showExportModal, setShowExportModal] = React.useState(false);
  const [exportTarget, setExportTarget] = React.useState<
    ResumeDbModel | undefined
  >(undefined);
  const [exportTheme, setExportTheme] = React.useState<string>(resumeThemes[0]);
  const [showImportFromPlatformsModal, setShowImportFromPlatformsModal] =
    React.useState(false);
  const [showImportModal, setShowImportModal] = React.useState(false);
  const [importText, setImportText] = React.useState('');
  const [importError, setImportError] = React.useState<string | undefined>(
    undefined,
  );
  const [importModel, setImportModel] = React.useState<
    ResumeDbModel | undefined
  >(undefined);
  const [importSaving, setImportSaving] = React.useState(false);

  // Critique modal state
  const [showCritiqueModal, setShowCritiqueModal] = React.useState(false);
  const [critiqueTargetId, setCritiqueTargetId] = React.useState<
    string | undefined
  >(undefined);
  const [critiqueTargetName, setCritiqueTargetName] = React.useState<
    string | undefined
  >(undefined);
  const [critiqueLoading, setCritiqueLoading] = React.useState(false);
  const [critiqueError, setCritiqueError] = React.useState<string | undefined>(
    undefined,
  );
  const [critiqueResult, setCritiqueResult] = React.useState<
    ResumeCritique | undefined
  >(undefined);
  const [critiqueBaseResume, setCritiqueBaseResume] = React.useState<
    ResumeDbModel | undefined
  >(undefined);
  const [createFromCritiqueLoading, setCreateFromCritiqueLoading] =
    React.useState(false);
  const [generatedResume, setGeneratedResume] = React.useState<
    ResumeDbModel | undefined
  >(undefined);
  const [saveGeneratedLoading, setSaveGeneratedLoading] = React.useState(false);
  const [generatedTheme, setGeneratedTheme] = React.useState<string>(
    resumeThemes[0],
  );

  // Optional critique parameters
  const [critiqueTargetJobTitle, setCritiqueTargetJobTitle] =
    React.useState<string>('');
  const [critiqueTargetIndustry, setCritiqueTargetIndustry] =
    React.useState<string>('');
  const [critiqueExtraInstructions, setCritiqueExtraInstructions] =
    React.useState<string>('');

  const [searchParameters, setSearchParameters] = useSearchParams();
  const [page, setPage] = React.useState<number>(() => {
    const sp = Number(searchParameters.get('page') ?? '1');
    return Number.isFinite(sp) && sp > 0 ? sp : 1;
  });
  const [perPage, setPerPage] = React.useState<number>(() => {
    const pp = Number(searchParameters.get('per_page') ?? '20');
    return Number.isFinite(pp) && pp > 0 ? pp : 20;
  });

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isDev = (() => {
    const raw = process.env.REACT_APP_DEV;
    return raw === 'true' || raw === '1';
  })();

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      const response = await axios.get<ResumeListInfo[]>('/api/resumes', {
        params: {page, per_page: perPage},
      });
      setItems(response.data ?? []);
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to load resumes'));
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    setSearchParameters({page: String(page), per_page: String(perPage)});
  }, [page, perPage, setSearchParameters]);

  const handleDelete = async (id?: string) => {
    if (!id) {
      return;
    }

    if (!globalThis.confirm('Delete this resume?')) {
      return;
    }

    try {
      await axios.delete(`/api/resumes/${id}`);
      setItems(previous => previous.filter(it => it.id !== id));
    } catch (error_: unknown) {
      alert('Failed to delete: ' + getErrorMessage(error_, 'Unknown error'));
    }
  };

  const handleExport = async (id?: string) => {
    if (!id) {
      return;
    }

    try {
      setExportingId(id);
      setError(undefined);
      const response = await axios.get<ResumeDbModel>(`/api/resumes/${id}`);
      const full = response.data;
      if (!full) {
        alert('Resume not found');
        return;
      }

      // Open modal to choose theme with the full resume model
      setExportTarget(full);
      setExportTheme(resumeThemes[0]);
      setShowExportModal(true);
    } catch (error_: unknown) {
      alert(
        'Failed to load resume: ' + getErrorMessage(error_, 'Unknown error'),
      );
    } finally {
      setExportingId(undefined);
    }
  };

  const handleExportJson = async (id?: string) => {
    if (!id) {
      return;
    }

    try {
      setExportingJsonId(id);
      setError(undefined);
      const response = await axios.get<ResumeDbModel>(`/api/resumes/${id}`);
      const full = response.data;
      if (!full) {
        alert('Resume not found');
        return;
      }

      const resumeOnly: ResumeSchema = full.resume ?? {};
      const json = JSON.stringify(resumeOnly, null, 2);
      const blob = new Blob([json], {type: 'application/json'});
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (full.name || 'resume').replaceAll(/[^\w\-.]/gi, '_');
      a.download = `${safeName}.json`;
      document.body.append(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(url);
      try {
        dispatch(setHasExported(true));
      } catch {}
    } catch (error_: unknown) {
      alert(
        'Failed to export JSON: ' + getErrorMessage(error_, 'Unknown error'),
      );
    } finally {
      setExportingJsonId(undefined);
    }
  };

  const performExport = async (theme: string, r: ResumeDbModel) => {
    if (!r) {
      alert('Resume not found');
      setShowExportModal(false);
      return;
    }

    try {
      setShowExportModal(false);
      setExportingId(r.id ?? null);
      setError(undefined);
      // POSTing the resume to the export endpoint and request a PDF blob
      const response = await axios.post<Blob>(
        '/api/export_resume_to_pdf',
        {resume_model: r},
        {params: {resume_theme: theme}, responseType: 'blob'},
      );
      const blob = response.data;
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (r.name || 'resume').replaceAll(/[^\w\-.]/gi, '_');
      a.download = `${safeName}.pdf`;
      document.body.append(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(url);
      try {
        dispatch(setHasExported(true));
      } catch {}
    } catch (error_: unknown) {
      alert('Failed to export: ' + getErrorMessage(error_, 'Unknown error'));
    } finally {
      setExportingId(undefined);
      setExportTarget(undefined);
    }
  };

  // Critique helpers
  const openCritique = (id?: string, name?: string) => {
    if (!id) {
      return;
    }

    setCritiqueTargetId(id);
    setCritiqueTargetName(name ?? undefined);
    setCritiqueResult(undefined);
    setCritiqueBaseResume(undefined);
    setGeneratedResume(undefined);
    setCritiqueError(undefined);
    setCritiqueTargetJobTitle('');
    setCritiqueTargetIndustry('');
    setCritiqueExtraInstructions('');
    setShowCritiqueModal(true);
  };

  const runCritique = async () => {
    if (!critiqueTargetId) {
      return;
    }

    try {
      setCritiqueLoading(true);
      setCritiqueError(undefined);
      // Load the full resume model
      const response = await axios.get<ResumeDbModel>(
        `/api/resumes/${critiqueTargetId}`,
      );
      const full = response.data;
      if (!full) {
        throw new Error('Resume not found');
      }

      setCritiqueBaseResume(full);
      // Read and store the base resume theme so generated resumes can inherit it
      try {
        const baseThemeKey = `resume_theme_${full.id ?? 'global'}`;
        const savedBaseTheme = localStorage.getItem(baseThemeKey);
        setGeneratedTheme(
          isStringOption(savedBaseTheme, resumeThemesNew)
            ? savedBaseTheme
            : resumeThemes[0],
        );
      } catch {
        setGeneratedTheme(resumeThemes[0]);
      }

      // Call AI critique endpoint (with optional targeting params)
      const resp = await axios.post<ResumeCritique>(
        '/api/ai/critique_resume',
        full,
        {
          params: {
            target_job_title: critiqueTargetJobTitle || undefined,
            target_industry: critiqueTargetIndustry || undefined,
            extra_instructions: critiqueExtraInstructions || undefined,
          },
        },
      );
      setCritiqueResult(resp.data);
    } catch (error_: unknown) {
      setCritiqueError(getErrorMessage(error_, 'Failed to run critique'));
    } finally {
      setCritiqueLoading(false);
    }
  };

  const createResumeFromCritique = async () => {
    if (!critiqueResult || !critiqueBaseResume) {
      return;
    }

    try {
      setCreateFromCritiqueLoading(true);
      setCritiqueError(undefined);
      const resp = await axios.post<ResumeDbModel>(
        '/api/ai/create_resume_from_critique',
        {
          resume_model: critiqueBaseResume,
          resume_critique: critiqueResult,
        },
      );
      setGeneratedResume(resp.data);
      // Keep the base resume theme selected for the generated draft
      try {
        const baseThemeKey = `resume_theme_${critiqueBaseResume.id ?? 'global'}`;
        const savedBaseTheme = localStorage.getItem(baseThemeKey);
        const themeToUse = isStringOption(savedBaseTheme, resumeThemesNew)
          ? savedBaseTheme
          : generatedTheme;
        setGeneratedTheme(themeToUse);
      } catch {
        // ignore
      }
    } catch (error_: unknown) {
      setCritiqueError(
        getErrorMessage(error_, 'Failed to create resume from critique'),
      );
    } finally {
      setCreateFromCritiqueLoading(false);
    }
  };

  const saveGenerated = async (v?: ResumeDbModel) => {
    const payload = v ?? generatedResume;
    if (!payload) {
      return;
    }

    const validationErrors = validateResumePayload(payload);
    if (validationErrors.summary.length > 0) {
      setCritiqueError(
        `Fix these resume format issues: ${validationErrors.summary.join(' ')}`,
      );
      return;
    }

    try {
      setSaveGeneratedLoading(true);
      const response = await axios.post<ResumeDbModel>('/api/resumes', payload);
      const created = response.data;
      try {
        dispatch(setHasResume(true));
      } catch {}

      // Persist the inherited theme for the newly created resume id
      try {
        const newId = created?.id;
        if (newId) {
          localStorage.setItem(`resume_theme_${newId}`, generatedTheme);
        }
      } catch {
        // ignore storage errors
      }

      setShowCritiqueModal(false);
      setGeneratedResume(undefined);
      if (created?.id) {
        void navigate(`/resumes/${created.id}`);
      } else {
        await load();
      }
    } catch (error_: unknown) {
      setCritiqueError(
        `Failed to save generated resume: ${getErrorMessage(
          error_,
          'Unknown error',
        )}`,
      );
    } finally {
      setSaveGeneratedLoading(false);
    }
  };

  const discardGenerated = () => {
    if (saveGeneratedLoading) {
      return;
    }

    setGeneratedResume(undefined);
  };

  const openImportModal = () => {
    setImportText('');
    setImportError(undefined);
    setImportModel(undefined);
    setShowImportModal(true);
  };

  const handleImportFile = (file?: File) => {
    if (!file) {
      return;
    }

    setImportError(undefined);
    void file
      .text()
      .then(text => {
        setImportText(text);
      })
      .catch(() => {
        setImportError('Failed to read file');
      });
  };

  const parseImport = () => {
    try {
      setImportError(undefined);
      const parsed: unknown = JSON.parse(importText);
      if (!parsed || typeof parsed !== 'object') {
        throw new Error('JSON must be an object');
      }

      // Require raw ResumeSchema (NOT an object with a 'resume' key)
      if ('resume' in parsed) {
        throw new Error(
          "Please provide a raw Resume JSON, not an object with a 'resume' field.",
        );
      }

      if (!isResumeSchema(parsed)) {
        throw new Error('Invalid resume JSON');
      }

      const resume = parsed;
      const derivedName =
        typeof resume.basics?.name === 'string' && resume.basics.name
          ? String(resume.basics.name)
          : 'imported';
      setImportModel({
        name: derivedName,
        id: '',
        user_id: '',
        resume,
      });
    } catch (error_: unknown) {
      setImportError(getErrorMessage(error_, 'Invalid JSON'));
    }
  };

  const saveImport = async (v?: ResumeDbModel) => {
    const payload = v ?? importModel;
    if (!payload) {
      return;
    }

    const validationErrors = validateResumePayload(payload);
    if (validationErrors.summary.length > 0) {
      setImportError(
        `Fix these resume format issues: ${validationErrors.summary.join(' ')}`,
      );
      return;
    }

    try {
      setImportSaving(true);
      await axios.post<ResumeDbModel>('/api/resumes', payload);
      // Success: reset and refresh list
      setShowImportModal(false);
      setImportModel(undefined);
      setImportText('');
      try {
        dispatch(setHasResume(true));
      } catch {}

      await load();
    } catch (error_: unknown) {
      alert('Failed to create: ' + getErrorMessage(error_, 'Unknown error'));
    } finally {
      setImportSaving(false);
    }
  };

  const discardImport = () => {
    setImportModel(undefined);
    setImportText('');
    setImportError(undefined);
    setShowImportModal(false);
  };

  return (
    <Container className='my-5'>
      <Stack direction='horizontal' className='mb-3' gap={2}>
        <h1 className='mb-0'>Resumes</h1>
        <div className='ms-auto'>
          <div className='d-none d-md-flex align-items-center'>
            <Link to='new' className='btn btn-primary btn-sm me-2 icon-btn'>
              <FaPlusCircle size={16} />
              Create Resume
            </Link>
            <Link to='ai' className='btn btn-secondary btn-sm me-2 icon-btn'>
              <VscSparkleFilled size={16} className='typst-svg-override' />
              Create Resume w/ AI
            </Link>
            <Link
              to='import-pdf'
              className='btn btn-outline-secondary btn-sm me-2 icon-btn'
            >
              <FaFilePdf size={16} />
              Import PDF
            </Link>
            <Button
              variant='outline-secondary'
              className='btn-sm icon-btn me-2'
              onClick={() => {
                setShowImportFromPlatformsModal(true);
              }}
            >
              Import from
              <FaLinkedin size={16} style={{color: '#0a66c2'}} />
              <SiIndeed size={16} style={{color: '#2557a7'}} />
            </Button>
            <Button
              variant='outline-secondary'
              className='btn-sm icon-btn me-2'
              onClick={openImportModal}
            >
              <LuFileJson size={16} />
              Import JSON
            </Button>
          </div>

          <div className='d-flex d-md-none'>
            <Dropdown align='end'>
              <Dropdown.Toggle
                variant='secondary'
                size='sm'
                id='resumes-actions-dropdown'
              >
                Create
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item as={Link} to='new' className='icon-btn'>
                  <FaPlusCircle size={16} />
                  Create Resume
                </Dropdown.Item>
                <Dropdown.Item as={Link} to='ai' className='icon-btn'>
                  <VscSparkleFilled size={16} className='typst-svg-override' />{' '}
                  Create Resume w/ AI
                </Dropdown.Item>
                <Dropdown.Item as={Link} to='import-pdf' className='icon-btn'>
                  <FaFilePdf size={16} />
                  Import PDF
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    setShowImportFromPlatformsModal(true);
                  }}
                  className='icon-btn'
                >
                  Import from
                  <FaLinkedin size={16} style={{color: '#0a66c2'}} />
                  <SiIndeed size={16} style={{color: '#2557a7'}} />
                </Dropdown.Item>
                <Dropdown.Item onClick={openImportModal} className='icon-btn'>
                  <LuFileJson size={16} />
                  Import JSON
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </Stack>

      {loading && (
        <div className='d-flex alignments-center'>
          <Spinner animation='border' size='sm' className='me-2' />
          <span>Loading...</span>
        </div>
      )}
      {error && <Alert variant='danger'>{error}</Alert>}

      {!loading && !error && (
        <Table responsive striped bordered hover size='sm'>
          <thead>
            <tr>
              <th>Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={2}>No resumes found.</td>
              </tr>
            )}
            {items.map(it => (
              <tr key={it.id ?? it.name}>
                <td style={{whiteSpace: 'normal'}}>
                  {it.id ? <Link to={`${it.id}`}>{it.name}</Link> : it.name}
                </td>

                <td style={{whiteSpace: 'normal'}}>
                  {/* Full inline action buttons for md+ */}
                  <div className='d-none d-md-flex'>
                    <Stack direction='horizontal' gap={2}>
                      {it.id && (
                        <OverlayTrigger
                          placement='top'
                          delay={{show: 250, hide: 400}}
                          overlay={<Tooltip>Edit</Tooltip>}
                        >
                          <Link
                            to={`${it.id}`}
                            className='btn btn-outline-secondary btn-sm'
                          >
                            <FaEdit size={24} />
                          </Link>
                        </OverlayTrigger>
                      )}
                      <Button
                        variant='outline-primary'
                        size='sm'
                        onClick={() => {
                          if (it.id) {
                            void navigate(`preview/${it.id}`);
                          }
                        }}
                        disabled={!it.id}
                        className='icon-btn'
                      >
                        <FaFilePdf size={14} className='me-1' />
                        View/Export PDF
                      </Button>
                      {isDev ? (
                        <Button
                          variant='outline-secondary'
                          size='sm'
                          onClick={() => {
                            void handleExport(it.id);
                          }}
                          disabled={!it.id}
                          className='icon-btn'
                        >
                          {exportingId === it.id ? (
                            <>
                              <Spinner
                                animation='border'
                                size='sm'
                                className='me-2'
                              />
                              Exporting...
                            </>
                          ) : (
                            <>
                              <FaFilePdf size={14} className='me-1' />
                              Export PDF (legacy)
                            </>
                          )}
                        </Button>
                      ) : null}
                      <Button
                        variant='outline-secondary'
                        size='sm'
                        onClick={() => {
                          void handleExportJson(it.id);
                        }}
                        disabled={!it.id}
                        className='icon-btn'
                      >
                        {exportingJsonId === it.id ? (
                          <>
                            <Spinner
                              animation='border'
                              size='sm'
                              className='me-2'
                            />
                            Exporting...
                          </>
                        ) : (
                          <>
                            <LuFileJson size={16} />
                            Export JSON
                          </>
                        )}
                      </Button>
                      <Button
                        variant='outline-secondary'
                        size='sm'
                        onClick={() => {
                          openCritique(it.id, it.name);
                        }}
                        disabled={!it.id}
                        className='icon-btn'
                      >
                        <VscSparkleFilled
                          size={16}
                          className='typst-svg-override'
                        />
                        AI Critique
                      </Button>
                      <OverlayTrigger
                        placement='top'
                        delay={{show: 250, hide: 400}}
                        overlay={<Tooltip>Delete</Tooltip>}
                      >
                        <Button
                          variant='outline-danger'
                          size='sm'
                          onClick={() => {
                            void handleDelete(it.id);
                          }}
                          disabled={!it.id}
                        >
                          <FaTrash size={24} />
                        </Button>
                      </OverlayTrigger>
                    </Stack>
                  </div>

                  {/* Compact dropdown for small screens (Floating UI to prevent clipping) */}
                  <MobileRowMenu
                    disabled={!it.id}
                    onEdit={
                      it.id
                        ? () => {
                            void navigate(`${it.id}`);
                          }
                        : undefined
                    }
                    onViewExport={
                      it.id
                        ? () => {
                            void navigate(`preview/${it.id}`);
                          }
                        : undefined
                    }
                    onExport={
                      isDev && it.id
                        ? () => {
                            void handleExport(it.id);
                          }
                        : undefined
                    }
                    onExportJson={
                      it.id
                        ? () => {
                            void handleExportJson(it.id);
                          }
                        : undefined
                    }
                    onCritique={
                      it.id
                        ? () => {
                            openCritique(it.id, it.name);
                          }
                        : undefined
                    }
                    onDelete={
                      it.id
                        ? () => {
                            void handleDelete(it.id);
                          }
                        : undefined
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {!loading && !error && (
        <div className='d-flex align-items-center justify-content-between mt-2'>
          <div className='d-flex alignments-center'>
            <Form.Label className='me-2 mb-0'>Rows per page</Form.Label>
            <Form.Select
              size='sm'
              style={{width: 'auto'}}
              value={perPage}
              onChange={event => {
                const next = Number(event.currentTarget.value);
                setPerPage(next);
                setPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </div>
          <div className='d-flex align-items-center'>
            <span className='me-3'>Page {page}</span>
            <Pagination className='mb-0'>
              <Pagination.Prev
                onClick={() => {
                  setPage(p => Math.max(1, p - 1));
                }}
                disabled={page <= 1 || loading}
              />
              <Pagination.Next
                onClick={() => {
                  setPage(p => p + 1);
                }}
                disabled={loading || items.length < perPage}
              />
            </Pagination>
          </div>
        </div>
      )}
      <ImportFromPlatformsModal
        show={showImportFromPlatformsModal}
        onHide={() => {
          setShowImportFromPlatformsModal(false);
        }}
      />
      <Modal
        show={showExportModal}
        onHide={() => {
          setShowExportModal(false);
          setExportTarget(undefined);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Export '{exportTarget?.name ?? 'resume'}' to PDF
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId='resumeThemeSelect'>
              <Form.Label>Theme</Form.Label>
              <Form.Select
                value={exportTheme}
                onChange={event => {
                  setExportTheme(event.currentTarget.value);
                }}
              >
                {resumeThemes.map(t => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => {
              setShowExportModal(false);
              setExportTarget(undefined);
            }}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              if (exportTarget) {
                void performExport(exportTheme, exportTarget);
              }
            }}
          >
            Export
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCritiqueModal}
        onHide={() => {
          setShowCritiqueModal(false);
          setCritiqueResult(undefined);
          setCritiqueBaseResume(undefined);
          setGeneratedResume(undefined);
          setCritiqueError(undefined);
          setCritiqueLoading(false);
          setCreateFromCritiqueLoading(false);
          setCritiqueTargetJobTitle('');
          setCritiqueTargetIndustry('');
          setCritiqueExtraInstructions('');
          setGeneratedTheme(resumeThemes[0]);
        }}
        size='lg'
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <span className='d-inline-flex align-items-center'>
              <VscSparkleFilled
                size={24}
                style={{marginRight: 8}}
                className='typst-svg-override-lg'
              />
              AI Resume Critique{' '}
              {critiqueTargetName ? `— ${critiqueTargetName}` : ''}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {critiqueError && <Alert variant='danger'>{critiqueError}</Alert>}

          {generatedResume && (
            <div>
              <Alert variant='info'>
                A new resume was generated from the critique. You can edit it
                below, then save or discard.
              </Alert>
              <ResumeForm
                value={generatedResume}
                onChange={v => {
                  setGeneratedResume(v);
                }}
                onSubmit={v => {
                  void saveGenerated(v);
                }}
                readOnly={false}
                showActions={false}
              />
            </div>
          )}

          {!generatedResume && (
            <Form className='mb-3'>
              <Row className='g-2'>
                <Col md={6}>
                  <Form.Group controlId='critique-target-job-title'>
                    <Form.Label>Target job title (optional)</Form.Label>
                    <Form.Control
                      type='text'
                      value={critiqueTargetJobTitle}
                      onChange={event => {
                        setCritiqueTargetJobTitle(event.currentTarget.value);
                      }}
                      placeholder='e.g., Senior Frontend Engineer'
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId='critique-target-industry'>
                    <Form.Label>Target industry (optional)</Form.Label>
                    <Form.Control
                      type='text'
                      value={critiqueTargetIndustry}
                      onChange={event => {
                        setCritiqueTargetIndustry(event.currentTarget.value);
                      }}
                      placeholder='e.g., FinTech'
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group
                className='mt-2'
                controlId='critique-extra-instructions'
              >
                <Form.Label>Extra instructions (optional)</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={3}
                  value={critiqueExtraInstructions}
                  onChange={event => {
                    setCritiqueExtraInstructions(event.currentTarget.value);
                  }}
                  placeholder='Any guidance for the critique (tone, focus areas, etc.)'
                />
              </Form.Group>
            </Form>
          )}

          {!generatedResume && critiqueLoading && (
            <div className='d-flex align-items-center mb-2'>
              <Spinner animation='border' size='sm' className='me-2' />
              <span>Running critique...</span>
            </div>
          )}
          {!generatedResume && createFromCritiqueLoading && (
            <div className='d-flex align-items-center mb-2'>
              <Spinner animation='border' size='sm' className='me-2' />
              <span>Creating resume...</span>
            </div>
          )}
          {!generatedResume && !critiqueLoading && !critiqueResult && (
            <Alert variant='info'>
              Click 'Run Critique' to analyze this resume.
            </Alert>
          )}
          {!generatedResume && critiqueResult && (
            <div style={{maxHeight: '60vh', overflowY: 'auto'}}>
              <h5 className='mb-2'>
                Overall Score: {critiqueResult.overall_score}
              </h5>
              <p>
                <strong>Summary:</strong> {critiqueResult.summary}
              </p>

              {critiqueResult.strengths?.length ? (
                <>
                  <h6>Strengths</h6>
                  <ul>
                    {critiqueResult.strengths.map((s, i) => (
                      <li key={`str-${i}`}>{s}</li>
                    ))}
                  </ul>
                </>
              ) : null}

              {critiqueResult.weaknesses?.length ? (
                <>
                  <h6>Weaknesses</h6>
                  <ul>
                    {critiqueResult.weaknesses.map((w, i) => (
                      <li key={`weak-${i}`}>{w}</li>
                    ))}
                  </ul>
                </>
              ) : null}

              {critiqueResult.section_scores ? (
                <>
                  <h6>Section Scores</h6>
                  <ul>
                    <li>Contact: {critiqueResult.section_scores.contact}/10</li>
                    <li>
                      Headline/Summary:{' '}
                      {critiqueResult.section_scores.headline_summary}/10
                    </li>
                    <li>
                      Experience: {critiqueResult.section_scores.experience}/10
                    </li>
                    <li>
                      Education: {critiqueResult.section_scores.education}/10
                    </li>
                    <li>Skills: {critiqueResult.section_scores.skills}/10</li>
                    <li>
                      Formatting: {critiqueResult.section_scores.formatting}/10
                    </li>
                    <li>
                      ATS Friendliness:{' '}
                      {critiqueResult.section_scores.ats_friendliness}/10
                    </li>
                    <li>Grammar: {critiqueResult.section_scores.grammar}/10</li>
                  </ul>
                </>
              ) : null}

              {critiqueResult.prioritized_actions?.length ? (
                <>
                  <h6>Prioritized Actions</h6>
                  <ol>
                    {critiqueResult.prioritized_actions.map((a, i) => (
                      <li key={`act-${i}`}>
                        <div>
                          <strong>Priority {a.priority}:</strong>{' '}
                          {a.description}{' '}
                          <span className='text-muted'>
                            ({a.estimated_time}, {a.impact})
                          </span>
                        </div>
                      </li>
                    ))}
                  </ol>
                </>
              ) : null}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => {
              setShowCritiqueModal(false);
              setCritiqueResult(undefined);
              setCritiqueBaseResume(undefined);
              setGeneratedResume(undefined);
              setCritiqueError(undefined);
              setCritiqueLoading(false);
              setCreateFromCritiqueLoading(false);
              setCritiqueTargetJobTitle('');
              setCritiqueTargetIndustry('');
              setCritiqueExtraInstructions('');
              setGeneratedTheme(resumeThemes[0]);
            }}
            disabled={saveGeneratedLoading}
          >
            Close
          </Button>

          {generatedResume ? (
            <>
              <Button
                variant='outline-secondary'
                onClick={discardGenerated}
                disabled={saveGeneratedLoading}
              >
                Discard
              </Button>
              <Button
                variant='primary'
                onClick={() => {
                  void saveGenerated(generatedResume);
                }}
                disabled={saveGeneratedLoading}
              >
                {saveGeneratedLoading ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant='primary'
                onClick={() => {
                  void runCritique();
                }}
                disabled={critiqueLoading || !critiqueTargetId}
              >
                {critiqueLoading ? 'Running...' : 'Run Critique'}
              </Button>
              <Button
                variant='success'
                onClick={() => {
                  void createResumeFromCritique();
                }}
                disabled={
                  createFromCritiqueLoading ||
                  !critiqueResult ||
                  !critiqueBaseResume
                }
              >
                {createFromCritiqueLoading ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-2' />
                    Creating...
                  </>
                ) : (
                  'Create Resume from Critique'
                )}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={showImportModal}
        onHide={() => {
          setShowImportModal(false);
          setImportModel(undefined);
        }}
        size='lg'
      >
        <Modal.Header closeButton>
          <Modal.Title>Import Resume JSON</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!importModel && (
            <Form>
              <Form.Group controlId='importFile' className='mb-3'>
                <Form.Label>Choose JSON file (optional)</Form.Label>
                <Form.Control
                  type='file'
                  accept='application/json'
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const input = event.currentTarget;
                    const f =
                      input instanceof HTMLInputElement
                        ? input.files?.[0]
                        : undefined;
                    if (f) {
                      handleImportFile(f);
                    }
                  }}
                />
              </Form.Group>
              <Form.Group controlId='importText' className='mb-3'>
                <Form.Label>Or paste Resume JSON</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={12}
                  value={importText}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setImportText(event.currentTarget.value);
                  }}
                  placeholder='Paste raw Resume JSON here'
                />
              </Form.Group>
              {importError && <Alert variant='danger'>{importError}</Alert>}
            </Form>
          )}
          {importModel && (
            <div>
              <Alert variant='info'>
                Edit the imported resume below. Save to post it, or discard to
                cancel.
              </Alert>
              <ResumeForm
                value={importModel}
                onChange={v => {
                  setImportModel(v);
                }}
                onSubmit={v => {
                  void saveImport(v);
                }}
                readOnly={false}
                showActions={false}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {importModel ? (
            <>
              <Button
                variant='secondary'
                onClick={discardImport}
                disabled={importSaving}
              >
                Discard
              </Button>
              <Button
                variant='primary'
                onClick={() => {
                  void saveImport(importModel);
                }}
                disabled={importSaving}
              >
                {importSaving ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant='secondary'
                onClick={() => {
                  setShowImportModal(false);
                  setImportText('');
                  setImportError(undefined);
                }}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={parseImport}
                disabled={!importText}
              >
                Parse JSON
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

function ResumePreviewWrapper(): React.JSX.Element {
  const parameters = useParams();
  const resumeId = parameters.id;
  const navigate = useNavigate();
  const [model, setModel] = React.useState<ResumeDbModel | undefined>(
    undefined,
  );
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      if (!resumeId) {
        if (mounted) {
          setError('Invalid resume id');
          setLoading(false);
        }

        return;
      }

      try {
        setLoading(true);
        setError(undefined);
        const response = await axios.get<ResumeDbModel>(
          `/api/resumes/${resumeId}`,
        );
        if (mounted) {
          setModel(response.data);
        }
      } catch (error_: unknown) {
        if (mounted) {
          setError(getErrorMessage(error_, 'Failed to load resume'));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [resumeId]);

  if (loading) {
    return (
      <Container className='my-5 d-flex align-items-center'>
        <Spinner animation='border' />
        <span className='ms-2'>Loading preview...</span>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className='my-5'>
        <Alert variant='danger'>{error}</Alert>
      </Container>
    );
  }

  if (!model) {
    return (
      <Container className='my-5'>
        <Alert variant='warning'>Resume not found</Alert>
      </Container>
    );
  }

  return (
    <Container className='my-5'>
      <Stack direction='horizontal' className='mb-3' gap={2}>
        <Button
          variant='outline-secondary'
          onClick={() => {
            void navigate('/resumes');
          }}
        >
          Back
        </Button>
        <div className='ms-auto'>
          <Button
            variant='primary'
            className='icon-btn'
            onClick={() => {
              if (resumeId) {
                void navigate(`/resumes/${resumeId}`);
              }
            }}
          >
            <FaEdit
              size={16}
              className='typst-svg-override me-1 flex-shrink-0'
            />
            Edit Resume
          </Button>
        </div>
      </Stack>
      <ResumePreview model={model} defaultFitIfOverflow />
    </Container>
  );
}

const ResumePreview = React.memo(
  ({
    model,
    defaultFitIfOverflow = false,
  }: {
    model: ResumeDbModel;
    defaultFitIfOverflow?: boolean;
  }): React.JSX.Element => {
    const deferredResume = useDeferredValue(model?.resume);
    const [templateText, setTemplateText] = useState<string | undefined>(
      undefined,
    );

    // Zoom state (percent)
    const [zoom, setZoom] = useState<number>(100);
    const zoomStep = 10;
    const zoomMin = 20;
    const zoomMax = 300;

    // Mobile / fit-to-width behavior
    const containerRef = useRef<HTMLDivElement>(null);
    const pageRef = useRef<HTMLDivElement>(null);
    const [isMobile, setIsMobile] = useState<boolean>(() => {
      try {
        return globalThis.matchMedia('(max-width: 767.98px)').matches;
      } catch {
        return false;
      }
    });
    // Default mode: fit by default unless we only fit when overflow is detected
    const [fitMode, setFitMode] = useState<boolean>(
      () => !defaultFitIfOverflow,
    );
    const [fitZoom, setFitZoom] = useState<number | undefined>(undefined);
    const didAutoInitRef = useRef<boolean>(false);

    const increaseZoom = () => {
      setFitMode(false);
      setZoom(z => Math.min(zoomMax, z + zoomStep));
    };

    const decreaseZoom = () => {
      setFitMode(false);
      setZoom(z => Math.max(zoomMin, z - zoomStep));
    };

    // Keep isMobile up-to-date
    useEffect(() => {
      try {
        const mq = globalThis.matchMedia('(max-width: 767.98px)');
        const handler = (event: MediaQueryListEvent) => {
          setIsMobile(event.matches);
        };

        setIsMobile(mq.matches);
        mq.addEventListener('change', handler);

        return () => {
          mq.removeEventListener('change', handler);
        };
      } catch {
        // ignore on SSR / unsupported env
      }
    }, []);

    // Compute fit zoom based on container width and the rendered page width
    useEffect(() => {
      // A defensive ResizeObserver that never reads .clientWidth/.offsetWidth
      // from a null ref. Prefer using current DOM refs; if those are missing
      // (e.g. during unmount) fall back to using the ResizeObserver entries.
      const compute = (
        entries?: ResizeObserverEntry[] | ResizeObserverEntry,
      ) => {
        // Prefer actual DOM refs if available
        let containerW: number | undefined = containerRef.current?.clientWidth;
        let baseW: number | undefined =
          pageRef.current?.offsetWidth ?? 793.700_787;

        // If either measurement is missing, try to extract from the observer entries.
        if ((containerW === undefined || baseW === undefined) && entries) {
          const array = Array.isArray(entries) ? entries : [entries];
          for (const entry of array) {
            try {
              // ContentRect is a safe, cross-browser measurement provided by ResizeObserver
              const w = entry.contentRect?.width;
              if (w !== undefined) {
                containerW ??= w;
                baseW ??= w;
              }
            } catch {
              // ignore malformed entry
            }
          }
        }

        // If we still don't have both measurements, bail out (avoid reading null refs).
        if (containerW === undefined || baseW === undefined) {
          return;
        }

        const z = Math.max(
          zoomMin,
          Math.min(zoomMax, Math.round((containerW / baseW) * 100)),
        );
        setFitZoom(z);

        // On first measurement, decide whether to auto-fit only if overflow is detected.
        if (defaultFitIfOverflow && !didAutoInitRef.current) {
          if (z < 100) {
            setFitMode(true);
            setZoom(z);
          } else {
            setFitMode(false);
            setZoom(100);
          }

          didAutoInitRef.current = true;
          return;
        }

        if (fitMode) {
          setZoom(z);
        }
      };

      // Run an immediate compute if both refs exist
      if (containerRef.current && pageRef.current) {
        compute();
      }

      const MyResizeObserver = globalThis.ResizeObserver;
      if (!MyResizeObserver) {
        // ResizeObserver not supported -> skip live updates
        return;
      }

      const ro = new MyResizeObserver((entries: ResizeObserverEntry[]) => {
        // Pass entries into compute so it can safely fallback if refs are gone.
        compute(entries);
      });

      // Observe only nodes that currently exist (guard against null)
      if (containerRef.current) {
        ro.observe(containerRef.current);
      }

      if (pageRef.current) {
        ro.observe(pageRef.current);
      }

      // Disconnect on cleanup
      return () => {
        try {
          ro.disconnect();
        } catch {
          // ignore if already disconnected
        }
      };
    }, [fitMode, defaultFitIfOverflow]);

    // Reset initial auto-fit decision when model changes or prop toggles
    useEffect(() => {
      didAutoInitRef.current = false;
      if (defaultFitIfOverflow) {
        setFitMode(false);
        setZoom(100);
      }
    }, [model?.id, defaultFitIfOverflow]);

    const [selectedTheme, setSelectedTheme] = useResumeTheme(model?.id);
    // Fetch template text
    const themeMap = useMemo(() => {
      return new Map<string, string>([
        ['basic', '/templates/basic.typ.hbs'],
        ['caffeine', '/templates/caffeine.typ.hbs'],
        ['echo', '/templates/echo.typ.hbs'],
        ['frostline', '/templates/frostline.typ.hbs'],
        ['meadow', '/templates/meadow.typ.hbs'],
        ['react', '/templates/react.typ.hbs'],
        ['straightforward', '/templates/straightforward.typ.hbs'],
      ]);
    }, []);

    useEffect(() => {
      let mounted = true;
      (async () => {
        try {
          const path =
            themeMap.get(selectedTheme) ?? '/templates/basic.typ.hbs';
          const response = await fetch(path);
          const text = await response.text();
          if (mounted) {
            setTemplateText(text);
          }
        } catch {
          if (mounted) {
            setTemplateText(undefined);
          }
        }
      })();

      return () => {
        mounted = false;
      };
    }, [selectedTheme, themeMap]);

    // --- Worker Logic ---
    const workerRef = useRef<Worker | undefined>(null);
    const [svgContent, setSvgContent] = useState<string | undefined>(undefined);
    const [pages, setPages] = useState<string[]>([]);
    const [lastMainContent, setLastMainContent] = useState<string | undefined>(
      undefined,
    );
    const [isRendering, setIsRendering] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const dispatch = useAppDispatch();

    useEffect(() => {
      if (!svgContent) {
        setPages([]);
        return;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(svgContent, 'image/svg+xml');
      const svg = doc.querySelector('svg');
      if (!svg) {
        setPages([]);
        return;
      }

      const pageGroups = svg.querySelectorAll('g.typst-page');
      if (pageGroups.length === 0) {
        setPages([svgContent]);
        return;
      }

      const defs = svg.querySelector('defs')?.outerHTML ?? '';
      const style = svg.querySelector('style')?.outerHTML ?? '';
      const newPages: string[] = [];

      let pageGroupIndex = 0;
      while (pageGroupIndex < pageGroups.length) {
        const g = pageGroups.item(pageGroupIndex);
        pageGroupIndex += 1;

        if (!g) {
          continue;
        }

        if (!(g instanceof SVGGElement)) {
          continue;
        }

        const w = g.dataset.pageWidth;
        const h = g.dataset.pageHeight;
        g.removeAttribute('transform');

        const vb = w && h ? `viewBox='0 0 ${w} ${h}'` : '';
        const dims = w && h ? `width='${w}' height='${h}'` : '';

        newPages.push(`<svg ${vb} ${dims} xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'>
          ${defs}
          ${style}
          ${g.outerHTML}
        </svg>`);
      }

      setPages(newPages);
    }, [svgContent]);

    // Keep a ref to the model name so the worker callback always sees the latest one
    // without needing to recreate the worker.
    const modelNameRef = useRef(model?.name);
    useEffect(() => {
      modelNameRef.current = model?.name;
    }, [model?.name]);

    const downloadPdfBlob = React.useCallback(
      (data: Uint8Array, name?: string) => {
        let arrayBuffer: ArrayBuffer;
        if (data?.buffer instanceof ArrayBuffer) {
          if (
            data.byteOffset === 0 &&
            data.byteLength === data.buffer.byteLength
          ) {
            arrayBuffer = data.buffer;
          } else {
            arrayBuffer = data.buffer.slice(
              data.byteOffset,
              data.byteOffset + data.byteLength,
            );
          }
        } else {
          const copy = Uint8Array.from(data);
          arrayBuffer = copy.buffer;
        }

        const blob = new Blob([arrayBuffer], {type: 'application/pdf'});
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.target = '_blank';
        const safeName = (name ?? 'resume').replaceAll(/[^\w\-.]/gi, '_');
        link.download = `${safeName}.pdf`;
        document.body.append(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(link.href);
        try {
          dispatch(setHasExported(true));
        } catch {}
      },
      [dispatch],
    );

    useEffect(() => {
      if (process.env.NODE_ENV === 'test') {
        return;
      }

      let worker: Worker | undefined;
      let cancelled = false;

      const handleMessage = (event: MessageEvent<ResumeWorkerMessage>) => {
        const {data} = event;

        switch (data.type) {
          case 'success': {
            setSvgContent(data.svg);
            setLastMainContent(data.mainContent);
            setIsRendering(false);

            break;
          }

          case 'pdf_success': {
            downloadPdfBlob(data.pdfData, modelNameRef.current);
            setIsExporting(false);

            break;
          }

          case 'error': {
            console.error('Worker error:', data.message);
            setIsRendering(false);
            setIsExporting(false);

            break;
          }
        }
      };

      void (async () => {
        const {createResumeWorker} = await import('../workers/worker-utils');

        if (cancelled) {
          return;
        }

        worker = createResumeWorker();
        workerRef.current = worker;
        worker.addEventListener('message', handleMessage);
      })();

      return () => {
        cancelled = true;
        if (worker) {
          worker.removeEventListener('message', handleMessage);
          worker.terminate();
          if (workerRef.current === worker) {
            workerRef.current = undefined;
          }
        }
      };
    }, [downloadPdfBlob]);

    // Debounced render request
    useEffect(() => {
      if (!templateText || !deferredResume || !workerRef.current) {
        return;
      }

      setIsRendering(true);
      const timer = setTimeout(() => {
        workerRef.current?.postMessage({
          type: 'render',
          payload: {resume: deferredResume, templateText},
        });
      }, previewDebounceMs);

      return () => {
        clearTimeout(timer);
      };
    }, [deferredResume, templateText]);

    const handleExportPdf = () => {
      if (!lastMainContent || !workerRef.current) {
        return;
      }

      setIsExporting(true);
      workerRef.current.postMessage({
        type: 'pdf',
        payload: {mainContent: lastMainContent},
      });
    };

    return (
      <Container fluid style={{backgroundColor: '#e4e5ea'}}>
        <div className='d-flex align-items-center mb-2' style={{gap: 8}}>
          <Form.Label className='mb-0 me-2'>Theme</Form.Label>
          <Form.Select
            size='sm'
            style={{width: 'auto'}}
            value={selectedTheme}
            onChange={event => {
              setSelectedTheme(event.currentTarget.value);
            }}
          >
            <option value='basic'>basic</option>
            <option value='caffeine'>caffeine</option>
            <option value='echo'>echo</option>
            <option value='frostline'>frostline</option>
            <option value='meadow'>meadow</option>
            <option value='react'>react</option>
            <option value='straightforward'>straightforward</option>
          </Form.Select>

          {isMobile ? null : (
            <div className='d-flex align-items-center' style={{gap: 8}}>
              <Button
                size='sm'
                variant={fitMode ? 'secondary' : 'outline-secondary'}
                onClick={() => {
                  setFitMode(f => !f);
                  if (!fitMode && fitZoom) {
                    setZoom(fitZoom);
                  }
                }}
                title='Toggle Fit to Width'
              >
                Fit
              </Button>
              <div
                className='mx-2'
                style={{width: 1, height: 24, background: '#ccc'}}
              />
              <Button
                size='sm'
                variant='outline-secondary'
                onClick={decreaseZoom}
                disabled={zoom <= zoomMin}
                aria-label='Zoom out'
              >
                -
              </Button>
              <div
                style={{
                  minWidth: 64,
                  textAlign: 'center',
                  fontVariantNumeric: 'tabular-nums',
                  cursor: 'pointer',
                }}
                title='Double-click to reset zoom to 100%'
                onDoubleClick={() => {
                  setFitMode(false);
                  setZoom(100);
                }}
              >
                {zoom}%
              </div>
              <Button
                size='sm'
                variant='outline-secondary'
                onClick={increaseZoom}
                disabled={zoom >= zoomMax}
                aria-label='Zoom in'
              >
                +
              </Button>
              <Button
                size='sm'
                variant='outline-secondary'
                onClick={() => {
                  setFitMode(false);
                  setZoom(100);
                }}
                title='Reset zoom to 100%'
                className='ms-2'
              >
                Reset
              </Button>
            </div>
          )}

          <Button
            onClick={handleExportPdf}
            disabled={!lastMainContent || isRendering || isExporting}
          >
            {isExporting ? 'Generating PDF...' : 'Download PDF'}
          </Button>

          {isRendering && (
            <div className='ms-auto d-flex align-items-center'>
              <Spinner animation='border' size='sm' className='me-2' />
              <span>Rendering...</span>
            </div>
          )}
        </div>

        <div ref={containerRef} style={{overflow: 'auto'}}>
          <div style={{display: 'flex', justifyContent: 'center', padding: 12}}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top center',
              }}
            >
              {pages.map((html, i) => (
                <div
                  key={i}
                  ref={i === 0 ? pageRef : undefined}
                  className='resume-preview-output'
                  style={{
                    backgroundColor: '#FFFFFF',
                    width: isMobile ? '100%' : '210mm',
                    boxShadow: '0 0 15px rgba(0,0,0,0.1)',
                  }}
                  dangerouslySetInnerHTML={{__html: html}}
                />
              ))}
            </div>
          </div>
        </div>
        <style>{`
        .resume-preview-output svg {
          width: 100%;
          height: auto;
          display: block;
        }
      `}</style>
      </Container>
    );
  },
);

function ResumeEditor(): React.JSX.Element {
  const parameters = useParams();
  const navigate = useNavigate();
  const resumeId = parameters.id;
  const dispatch = useAppDispatch();
  const [model, setModel] = React.useState<ResumeDbModel | undefined>(
    undefined,
  );
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [saving, setSaving] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(true);
  const [isMobile, setIsMobile] = React.useState<boolean>(false);

  // Tailor-to-job modal state
  const [showTailorModal, setShowTailorModal] = React.useState(false);
  const [jobMode, setJobMode] = React.useState<'saved' | 'paste'>('saved');
  const [jobs, setJobs] = React.useState<JobDescriptionListInfo[]>([]);
  const [jobsLoading, setJobsLoading] = React.useState(false);
  const [jobsError, setJobsError] = React.useState<string | undefined>(
    undefined,
  );
  const [selectedJobId, setSelectedJobId] = React.useState<string>('');
  const [jobText, setJobText] = React.useState<string>('');
  const [strictness, setStrictness] = React.useState<
    'strict' | 'moderate' | 'loose'
  >('moderate');
  const [tailoring, setTailoring] = React.useState(false);
  const [tailoredModel, setTailoredModel] = React.useState<
    ResumeDbModel | undefined
  >(undefined);
  const [, setCreatedJob] = React.useState<JobDescriptionDbModel | undefined>(
    undefined,
  );
  const [tailorError, setTailorError] = React.useState<string | undefined>(
    undefined,
  );
  const [selectedPaths, setSelectedPaths] = React.useState<string[]>([]);
  const [tailorSaving, setTailorSaving] = React.useState(false);

  // AI Assistant (Critique) panel state
  const [showAssistant, setShowAssistant] = React.useState(false);
  const [assistantTab, setAssistantTab] = React.useState<
    'critique' | 'ats' | 'rewrite'
  >('critique');
  const [recentAts, setRecentAts] = React.useState<Set<string>>(new Set());
  const [critLoading, setCritLoading] = React.useState(false);

  // Rewrite tab state
  const [rewriteMode, setRewriteMode] = React.useState<
    | 'strengthen'
    | 'quantify'
    | 'shorten'
    | 'tone_formal'
    | 'tone_plain'
    | 'keyword_boost'
  >('strengthen');
  const [rewriteScopes, setRewriteScopes] = React.useState<
    Record<string, boolean>
  >({
    basics_summary: false,
    work_descriptions: false,
    work_summaries: false,
    work_highlights: true,
    project_descriptions: false,
    project_highlights: false,
    volunteer_summaries: false,
    volunteer_highlights: false,
  });
  const [rewriteRunning, setRewriteRunning] = React.useState(false);
  const [rewriteProgress, setRewriteProgress] = React.useState<{
    done: number;
    total: number;
  }>({done: 0, total: 0});
  const [rewriteSkipped, setRewriteSkipped] = React.useState<number>(0);
  const [rewriteError, setRewriteError] = React.useState<string | undefined>(
    undefined,
  );
  const [critError, setCritError] = React.useState<string | undefined>(
    undefined,
  );
  const [critique, setCritique] = React.useState<ResumeCritique | undefined>(
    undefined,
  );
  const [targetRoleIdx, setTargetRoleIdx] = React.useState<number>(0);

  // Optional critique parameters (AI Assistant panel)
  const [critTargetJobTitle, setCritTargetJobTitle] =
    React.useState<string>('');
  const [critTargetIndustry, setCritTargetIndustry] =
    React.useState<string>('');
  const [critExtraInstructions, setCritExtraInstructions] =
    React.useState<string>('');

  // Inline AI rewrite (per-field) state
  type RewriteMode =
    | 'quantify'
    | 'shorten'
    | 'strengthen'
    | 'tone_formal'
    | 'tone_plain'
    | 'keyword_boost';

  type AiRewriteResponse = {
    rewritten: string;
    alternatives?: string[] | undefined;
  };

  const [aiBusy, setAiBusy] = React.useState<string | undefined>(undefined);
  const [aiError, setAiError] = React.useState<string | undefined>(undefined);
  const [aiHistory, setAiHistory] = React.useState<
    Array<{path: Array<string | number>; prev: any; next: any}>
  >([]);

  function keyFromPath(p: Array<string | number>): string {
    return p.map(String).join('.');
  }

  async function rewriteAndApplyAtPath(
    path: Array<string | number>,
    text: string,
    mode: RewriteMode,
    meta?: {roleTitle?: string; targetKeywords?: string[]},
  ) {
    if (!model) {
      return;
    }

    setAiError(undefined);
    setAiBusy(keyFromPath(path));
    try {
      const payload: {
        text: string;
        mode: RewriteMode;
        context_resume: string;
        role_title?: string;
        target_keywords?: string[];
      } = {
        text,
        mode,
        context_resume: JSON.stringify(model.resume ?? {}),
      };
      if (meta?.roleTitle) {
        payload.role_title = meta.roleTitle;
      }

      if (meta?.targetKeywords?.length) {
        payload.target_keywords = meta.targetKeywords;
      }

      const resp = await axios.post<AiRewriteResponse>(
        '/api/ai/rewrite',
        payload,
      );
      const newText = resp.data.rewritten;

      const previous = getByPath(model, path);
      const nextObject = setByPathSimple(model, path, newText);
      if (isResumeDbModel(nextObject)) {
        setModel(nextObject);
      }

      setAiHistory(h => [...h, {path, prev: previous, next: newText}]);
    } catch (error_: unknown) {
      setAiError(getErrorMessage(error_, 'AI rewrite failed'));
    } finally {
      setAiBusy(undefined);
    }
  }

  function undoLastAi() {
    if (!model || aiHistory.length === 0) {
      return;
    }

    const last = aiHistory.at(-1);
    if (!last) {
      return;
    }

    const restored = setByPathSimple(model, last.path, last.prev);
    if (isResumeDbModel(restored)) {
      setModel(restored);
    }

    setAiHistory(h => h.slice(0, -1));
  }

  // Collect targets for bulk rewrite based on selected scopes
  function collectRewriteTargets(scopes: Record<string, boolean>): Array<{
    path: Array<string | number>;
    text: string;
    meta?: {roleTitle?: string};
  }> {
    const targets: Array<{
      path: Array<string | number>;
      text: string;
      meta?: {roleTitle?: string};
    }> = [];
    const resume = model?.resume ?? {};

    // Basics
    if (scopes.basics_summary) {
      const s = resume.basics?.summary;
      if (typeof s === 'string' && s.trim()) {
        targets.push({path: ['resume', 'basics', 'summary'], text: s});
      }
    }

    // Work
    const work = ensureArray(resume.work ?? undefined);
    let workIndex = 0;
    for (const w of work) {
      const i = workIndex;
      const roleTitle = String(w?.position ?? w?.name ?? '');
      if (
        scopes.work_descriptions &&
        typeof w?.description === 'string' &&
        w.description.trim()
      ) {
        targets.push({
          path: ['resume', 'work', i, 'description'],
          text: w.description,
          meta: {roleTitle},
        });
      }

      if (
        scopes.work_summaries &&
        typeof w?.summary === 'string' &&
        w.summary.trim()
      ) {
        targets.push({
          path: ['resume', 'work', i, 'summary'],
          text: w.summary,
          meta: {roleTitle},
        });
      }

      if (scopes.work_highlights && Array.isArray(w?.highlights)) {
        let highlightIndex = 0;
        for (const h of w.highlights) {
          const j = highlightIndex;
          if (typeof h === 'string' && h.trim()) {
            targets.push({
              path: ['resume', 'work', i, 'highlights', j],
              text: h,
              meta: {roleTitle},
            });
          }

          highlightIndex += 1;
        }
      }

      workIndex += 1;
    }

    // Projects
    const projects = ensureArray(resume.projects ?? undefined);
    let projectIndex = 0;
    for (const p of projects) {
      const i = projectIndex;
      if (
        scopes.project_descriptions &&
        typeof p?.description === 'string' &&
        p.description.trim()
      ) {
        targets.push({
          path: ['resume', 'projects', i, 'description'],
          text: p.description,
        });
      }

      if (scopes.project_highlights && Array.isArray(p?.highlights)) {
        let highlightIndex = 0;
        for (const h of p.highlights) {
          const j = highlightIndex;
          if (typeof h === 'string' && h.trim()) {
            targets.push({
              path: ['resume', 'projects', i, 'highlights', j],
              text: h,
            });
          }

          highlightIndex += 1;
        }
      }

      projectIndex += 1;
    }

    // Volunteer
    const volunteer = ensureArray(resume.volunteer ?? undefined);
    let volunteerIndex = 0;
    for (const v of volunteer) {
      const i = volunteerIndex;
      const roleTitle = String(v?.position ?? v?.organization ?? '');
      if (
        scopes.volunteer_summaries &&
        typeof v?.summary === 'string' &&
        v.summary.trim()
      ) {
        targets.push({
          path: ['resume', 'volunteer', i, 'summary'],
          text: v.summary,
          meta: {roleTitle},
        });
      }

      if (scopes.volunteer_highlights && Array.isArray(v?.highlights)) {
        let highlightIndex = 0;
        for (const h of v.highlights) {
          const j = highlightIndex;
          if (typeof h === 'string' && h.trim()) {
            targets.push({
              path: ['resume', 'volunteer', i, 'highlights', j],
              text: h,
              meta: {roleTitle},
            });
          }

          highlightIndex += 1;
        }
      }

      volunteerIndex += 1;
    }

    return targets;
  }

  // Run bulk rewrite across selected fields sequentially
  async function runBulkRewrite() {
    if (!model) {
      return;
    }

    try {
      setRewriteError(undefined);
      const targets = collectRewriteTargets(rewriteScopes);
      setRewriteProgress({done: 0, total: targets.length});
      setRewriteSkipped(0);
      setRewriteRunning(true);

      let sequence = Promise.resolve();
      for (const target of targets) {
        sequence = sequence.then(async () => {
          const {text} = target;
          // Skip overly long fields to respect API maxLength (2000)
          if (!text || typeof text !== 'string' || text.length > 2000) {
            setRewriteSkipped(s => s + 1);
            setRewriteProgress(p => ({...p, done: p.done + 1}));
            return;
          }

          await rewriteAndApplyAtPath(
            target.path,
            text,
            rewriteMode,
            target.meta,
          );
          setRewriteProgress(p => ({...p, done: p.done + 1}));
        });
      }

      await sequence;
    } catch (error_: unknown) {
      setRewriteError(getErrorMessage(error_, 'Bulk rewrite failed'));
    } finally {
      setRewriteRunning(false);
    }
  }

  // Create a rewritten draft using critique, then use Tailor modal to review/apply changes
  async function rewriteUsingCritique() {
    if (!model) {
      return;
    }

    try {
      setRewriteError(undefined);
      if (!critique) {
        await runEditorCritique();
      }

      if (!critique) {
        return;
      }

      const resp = await axios.post<ResumeDbModel>(
        '/api/ai/create_resume_from_critique',
        {
          resume_model: model,
          resume_critique: critique,
        },
      );
      setTailoredModel(resp.data);
      setShowTailorModal(true);
    } catch (error_: unknown) {
      setRewriteError(
        getErrorMessage(error_, 'Failed to rewrite using critique'),
      );
    }
  }

  React.useEffect(() => {
    if (!showTailorModal || jobMode !== 'saved') {
      return;
    }

    setJobsLoading(true);
    setJobsError(undefined);
    (async () => {
      try {
        const response = await axios.get<JobDescriptionListInfo[]>(
          '/api/jobs',
          {
            params: {per_page: 100},
          },
        );
        setJobs(response.data ?? []);
      } catch (error_: unknown) {
        setJobsError(getErrorMessage(error_, 'Failed to load jobs'));
      } finally {
        setJobsLoading(false);
      }
    })();
  }, [showTailorModal, jobMode]);

  type Change = {
    path: Array<string | number>;
    before: unknown;
    after: unknown;
    kind: 'replace' | 'add' | 'remove';
  };

  const diffGeneric = React.useCallback(function diffGenericImpl(
    a: unknown,
    b: unknown,
    base: Array<string | number> = [],
    out: Change[] = [],
  ): Change[] {
    if (a === b) {
      return out;
    }

    if (Array.isArray(a) || Array.isArray(b)) {
      const aa = (Array.isArray(a) ? a : []) as unknown[];
      const bb = (Array.isArray(b) ? b : []) as unknown[];
      const length = Math.max(aa.length, bb.length);
      for (let i = 0; i < length; i++) {
        if (i >= aa.length) {
          out.push({
            path: [...base, i],
            before: undefined,
            after: bb[i],
            kind: 'add',
          });
        } else if (i >= bb.length) {
          out.push({
            path: [...base, i],
            before: aa[i],
            after: undefined,
            kind: 'remove',
          });
        } else {
          diffGenericImpl(aa[i], bb[i], [...base, i], out);
        }
      }

      return out;
    }

    if (!isRecord(a) || !isRecord(b)) {
      out.push({
        path: base,
        before: a,
        after: b,
        kind: a === undefined ? 'add' : b === undefined ? 'remove' : 'replace',
      });
      return out;
    }

    const aRecord = a;
    const bRecord = b;
    const keys = [...Object.keys(aRecord), ...Object.keys(bRecord)].filter(
      (value, index, array) => array.indexOf(value) === index,
    );
    for (const k of keys) {
      if (!(k in aRecord)) {
        out.push({
          path: [...base, k],
          before: undefined,
          after: bRecord[k],
          kind: 'add',
        });
      } else if (k in bRecord) {
        diffGenericImpl(aRecord[k], bRecord[k], [...base, k], out);
      } else {
        out.push({
          path: [...base, k],
          before: aRecord[k],
          after: undefined,
          kind: 'remove',
        });
      }
    }

    return out;
  }, []);

  function pathKey(p: Array<string | number>): string {
    return p.map(String).join('.');
  }

  function prettyPath(p: Array<string | number>): string {
    return p
      .map(seg => (typeof seg === 'number' ? `[${seg}]` : seg))
      .join('.')
      .replace('.[', '[');
  }

  function setByPathWithRemoval(
    object: unknown,
    path: Array<string | number>,
    value: unknown,
    kind: Change['kind'],
  ): unknown {
    if (path.length === 0) {
      return value;
    }

    const [head, ...rest] = path;
    if (isUnknownArray(object)) {
      const clone = [...object];
      const idx = typeof head === 'number' ? head : Number(head);
      if (rest.length === 0) {
        if (kind === 'remove') {
          if (idx >= 0 && idx < clone.length) {
            clone.splice(idx, 1);
          }
        } else {
          clone[idx] = value;
        }

        return clone;
      }

      clone[idx] = setByPathWithRemoval(clone[idx], rest, value, kind);
      return clone;
    }

    const clone: Record<string, unknown> = isRecord(object) ? {...object} : {};
    if (rest.length === 0) {
      if (kind === 'remove') {
        const key = String(head);
        const {[key]: _removed, ...next} = clone;
        return next;
      }

      clone[String(head)] = value;

      return clone;
    }

    clone[String(head)] = setByPathWithRemoval(
      clone[String(head)],
      rest,
      value,
      kind,
    );
    return clone;
  }

  function applyChanges(
    object: ResumeDbModel,
    changes: Change[],
  ): ResumeDbModel {
    let acc: unknown = object;

    for (const c of changes) {
      acc = setByPathWithRemoval(acc, c.path, c.after, c.kind);
    }

    return isResumeDbModel(acc) ? acc : object;
  }

  const tailorChanges = React.useMemo<Change[]>(() => {
    if (!model || !tailoredModel) {
      return [];
    }

    const a = model.resume ?? {};
    const b = tailoredModel.resume ?? {};
    return diffGeneric(a, b, ['resume'], []);
  }, [diffGeneric, model, tailoredModel]);

  React.useEffect(() => {
    if (tailoredModel) {
      setSelectedPaths([]);
    }
  }, [tailoredModel]);

  async function runTailor() {
    if (!model) {
      return;
    }

    setTailorError(undefined);
    setTailoring(true);
    try {
      let jobModel: JobDescriptionDbModel | undefined;
      if (jobMode === 'paste') {
        if (!jobText.trim()) {
          throw new Error('Please paste job description text.');
        }

        const jr = await axios.post<JobDescriptionDbModel>('/api/ai/jobs', {
          job_text: jobText,
        });
        jobModel = jr.data;
        setCreatedJob(jr.data);
      } else {
        const id = selectedJobId?.trim();
        if (!id) {
          throw new Error('Please select a job.');
        }

        const jr = await axios.get<JobDescriptionDbModel>(`/api/jobs/${id}`);
        jobModel = jr.data;
      }

      const resp = await axios.post<ResumeDbModel>(
        '/api/ai/resume_from_job_and_resume',
        {resume_model: model, job_model: jobModel},
        {params: {strictness}},
      );
      setTailoredModel(resp.data);
    } catch (error_: unknown) {
      setTailorError(getErrorMessage(error_, 'Failed to tailor resume'));
    } finally {
      setTailoring(false);
    }
  }

  async function acceptAllReplace() {
    if (!tailoredModel || !resumeId) {
      return;
    }

    try {
      setTailorSaving(true);
      const next = {...tailoredModel, id: resumeId};
      await axios.put(`/api/resumes/${resumeId}`, next);
      setModel(next);
      setShowTailorModal(false);
      setTailoredModel(undefined);
    } catch (error_: unknown) {
      alert('Failed to save: ' + getErrorMessage(error_, 'Unknown error'));
    } finally {
      setTailorSaving(false);
    }
  }

  async function applySelected() {
    if (!model || !tailoredModel || !resumeId) {
      return;
    }

    try {
      setTailorSaving(true);
      const sel = tailorChanges.filter(c =>
        selectedPaths.includes(pathKey(c.path)),
      );
      const nextModel = applyChanges(model, sel);
      const payload = {...nextModel, id: resumeId};
      await axios.put(`/api/resumes/${resumeId}`, payload);
      setModel(nextModel);
      setShowTailorModal(false);
      setTailoredModel(undefined);
    } catch (error_: unknown) {
      alert(
        'Failed to apply selected changes: ' +
          getErrorMessage(error_, 'Unknown error'),
      );
    } finally {
      setTailorSaving(false);
    }
  }

  async function saveAsNew() {
    if (!tailoredModel) {
      return;
    }

    try {
      setTailorSaving(true);
      const response = await axios.post<ResumeDbModel>(
        '/api/resumes',
        tailoredModel,
      );
      const created = response.data;
      // Inherit the current resume's theme for the newly created resume
      try {
        const baseKey = `resume_theme_${resumeId}`;
        const saved =
          localStorage.getItem(baseKey) ??
          localStorage.getItem('resume_theme_global');
        if (created?.id && isStringOption(saved, resumeThemesNew)) {
          localStorage.setItem(`resume_theme_${created.id}`, saved);
        }
      } catch {}

      if (created?.id) {
        void navigate(`/resumes/${created.id}`);
      } else {
        void navigate('/resumes');
      }

      setShowTailorModal(false);
      setTailoredModel(undefined);
    } catch (error_: unknown) {
      alert(
        'Failed to save as new: ' + getErrorMessage(error_, 'Unknown error'),
      );
    } finally {
      setTailorSaving(false);
    }
  }

  const toggleSelectAll = (all: boolean) => {
    if (all) {
      setSelectedPaths(tailorChanges.map(c => pathKey(c.path)));
    } else {
      setSelectedPaths([]);
    }
  };

  function normalizeCritique(c: ResumeCritique): ResumeCritique {
    const normalizedGap = (c.ats_keyword_gap ?? []).map(it => ({
      keyword: it.keyword,
      where_to_add: it.where_to_add,
    }));
    return {...c, ats_keyword_gap: normalizedGap};
  }

  async function runEditorCritique() {
    if (!model) {
      return;
    }

    try {
      setCritLoading(true);
      setCritError(undefined);
      const resp = await axios.post<ResumeCritique>(
        '/api/ai/critique_resume',
        model,
        {
          params: {
            target_job_title: critTargetJobTitle || undefined,
            target_industry: critTargetIndustry || undefined,
            extra_instructions: critExtraInstructions || undefined,
          },
        },
      );
      setCritique(normalizeCritique(resp.data));
    } catch (error_: unknown) {
      setCritError(getErrorMessage(error_, 'Failed to run critique'));
    } finally {
      setCritLoading(false);
    }
  }

  function parseLocationPath(loc: string): Array<string | number> {
    const segs: Array<string | number> = [];
    const parts = String(loc).split('.').filter(Boolean);
    for (const p of parts) {
      const re = /([^[\]]+)|\[(\d+)]/g;
      while (true) {
        const match = re.exec(p);
        if (!match) {
          break;
        }

        if (match[1]) {
          segs.push(match[1]);
        } else if (match[2]) {
          segs.push(Number(match[2]));
        }
      }
    }

    return segs;
  }

  function getByPath(object: unknown, path: Array<string | number>) {
    let current: unknown = object;
    for (const key of path) {
      if (current === null || current === undefined) {
        return current;
      }

      if (Array.isArray(current)) {
        const idx = typeof key === 'number' ? key : Number(key);
        current = current[idx];
        continue;
      }

      if (isRecord(current)) {
        current = current[String(key)];
        continue;
      }

      return current;
    }

    return current;
  }

  function setByPathSimple(
    object: unknown,
    path: Array<string | number>,
    value: unknown,
  ): unknown {
    if (path.length === 0) {
      return value;
    }

    const [head, ...rest] = path;
    if (isUnknownArray(object)) {
      const clone = [...object];
      const idx = typeof head === 'number' ? head : Number(head);
      clone[idx] = setByPathSimple(clone[idx], rest, value);
      return clone;
    }

    const clone: Record<string, unknown> = isRecord(object) ? {...object} : {};
    clone[String(head)] = setByPathSimple(clone[String(head)], rest, value);
    return clone;
  }

  function applyExactRewrite(rw: {
    location: string;
    original_text: string;
    replacement_text: string;
  }) {
    if (!model) {
      return;
    }

    const base = model.resume ?? {};
    const path = ['resume', ...parseLocationPath(rw.location)];
    const current = getByPath({resume: base}, path);
    // Optional safety check: ensure original_text matches current content
    if (
      rw.original_text &&
      typeof current === 'string' &&
      !current.includes(rw.original_text)
    ) {
      // Proceed anyway; alternatively show a toast/warning here.
    }

    const next = setByPathSimple({resume: base}, path, rw.replacement_text);
    if (isRecord(next) && isResumeSchema(next.resume)) {
      setModel({...model, resume: next.resume});
    }
  }

  function applyAllExactRewrites() {
    if (!critique?.exact_rewrites?.length) {
      return;
    }

    for (const rewrite of critique.exact_rewrites) {
      applyExactRewrite(rewrite);
    }
  }

  function ensureArray<T>(v: readonly T[] | undefined): T[] {
    const next: T[] = [];
    if (v) {
      for (const item of v) {
        next.push(item);
      }
    }

    return next;
  }

  function insertBulletsIntoWork(
    bullets: string[],
    roleIndex: number,
    asKeyword = false,
  ) {
    if (!model) {
      return;
    }

    const resume = {...model.resume};
    const work = ensureArray(resume.work ?? undefined);
    if (!work[roleIndex]) {
      return;
    }

    const role = {...work[roleIndex]};
    const highlights = ensureArray(role.highlights ?? undefined);
    const toAdd = asKeyword ? bullets.map(k => `• ${k}`) : bullets;
    role.highlights = [...highlights, ...toAdd];
    const nextWork = [...work];
    nextWork[roleIndex] = role;
    const next = {...resume, work: nextWork};
    setModel({...model, resume: next});
  }

  function addKeywordToSkills(keyword: string) {
    if (!model) {
      return;
    }

    const resume = {...model.resume};
    const skills = ensureArray(resume.skills ?? undefined);
    const exists = skills.some(
      s => (s?.name ?? '').toLowerCase() === String(keyword).toLowerCase(),
    );
    if (!exists) {
      skills.push({name: keyword});
      const next = {...resume, skills};
      setModel({...model, resume: next});
    }
  }

  async function saveEditorChanges() {
    if (!model || !resumeId) {
      return;
    }

    try {
      await axios.put(`/api/resumes/${resumeId}`, {...model, id: resumeId});
    } catch (error_: unknown) {
      alert('Failed to save: ' + getErrorMessage(error_, 'Unknown error'));
    }
  }

  // Debounced model for preview to prevent input lag
  const [debouncedModel, setDebouncedModel] = React.useState<
    ResumeDbModel | undefined
  >(undefined);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedModel(model);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [model]);

  React.useEffect(() => {
    try {
      const mq = globalThis.matchMedia('(max-width: 767.98px)');
      const handler = (event: MediaQueryListEvent) => {
        setIsMobile(event.matches);
      };

      setIsMobile(mq.matches);
      mq.addEventListener('change', handler);

      return () => {
        mq.removeEventListener('change', handler);
      };
    } catch {
      // ignore (e.g., SSR)
    }
  }, []);

  React.useEffect(() => {
    if (!resumeId) {
      setError('Invalid resume id');
      setLoading(false);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(undefined);
        const response = await axios.get<ResumeDbModel>(
          `/api/resumes/${resumeId}`,
        );
        if (mounted) {
          setModel(response.data);
        }
      } catch (error_: unknown) {
        if (mounted) {
          setError(getErrorMessage(error_, 'Failed to load resume'));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [resumeId]);

  const handleSubmit = async (v: ResumeDbModel) => {
    if (!resumeId) {
      return;
    }

    try {
      setSaving(true);
      await axios.put(`/api/resumes/${resumeId}`, {...v, id: resumeId});
      try {
        dispatch(setHasResume(true));
      } catch {}

      void navigate('/resumes');
    } catch (error_: unknown) {
      alert('Failed to save: ' + getErrorMessage(error_, 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!resumeId) {
      return;
    }

    if (!globalThis.confirm('Are you sure you want to delete this resume?')) {
      return;
    }

    try {
      await axios.delete(`/api/resumes/${resumeId}`);
      void navigate('/resumes');
    } catch (error_: unknown) {
      alert('Failed to delete: ' + getErrorMessage(error_, 'Unknown error'));
    }
  };

  return (
    <div className={showPreview && !isMobile ? 'my-5 mx-5' : 'my-5 container'}>
      <h1>Edit Resume</h1>
      <Stack direction='horizontal' className='mb-3' gap={2}>
        <Button
          variant='outline-secondary'
          onClick={() => {
            void navigate('/resumes');
          }}
        >
          Back
        </Button>

        {/* Desktop/tablet actions */}
        <div className='ms-auto d-none d-md-flex'>
          {aiHistory.length > 0 && (
            <Button
              size='sm'
              variant='outline-secondary'
              className='me-2'
              onClick={undoLastAi}
            >
              Undo last AI change
            </Button>
          )}
          <Button
            size='sm'
            variant='outline-primary'
            className='me-2 icon-btn'
            onClick={() => {
              setShowAssistant(true);
            }}
          >
            <VscSparkleFilled
              size={16}
              className='typst-svg-override me-1 flex-shrink-0'
            />
            AI Assistant
          </Button>
          <Button
            size='sm'
            variant='primary'
            className='me-2 icon-btn'
            onClick={() => {
              setShowTailorModal(true);
            }}
          >
            <VscSparkleFilled
              size={16}
              className='typst-svg-override me-1 flex-shrink-0'
            />
            AI Tailor to Job
          </Button>
          <Button
            size='sm'
            variant={showPreview ? 'primary' : 'outline-secondary'}
            onClick={() => {
              setShowPreview(s => !s);
            }}
            className='me-2'
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
          <Button
            size='sm'
            variant='outline-danger'
            className='me-2'
            onClick={() => {
              void handleDelete();
            }}
          >
            <FaTrash size={14} className='typst-svg-override me-2' />
            Delete
          </Button>
        </div>

        {/* Mobile actions */}
        <div className='ms-auto d-flex d-md-none'>
          <Dropdown align='end'>
            <Dropdown.Toggle variant='secondary' size='sm' id='editor-actions'>
              Actions
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {aiHistory.length > 0 && (
                <Dropdown.Item onClick={undoLastAi}>
                  Undo last AI change
                </Dropdown.Item>
              )}
              <Dropdown.Item
                onClick={() => {
                  setShowAssistant(true);
                }}
              >
                <VscSparkleFilled
                  size={16}
                  className='typst-svg-override me-2'
                />
                AI Assistant
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  setShowTailorModal(true);
                }}
              >
                <VscSparkleFilled
                  size={16}
                  className='typst-svg-override me-2'
                />
                AI Tailor to Job
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() => {
                  if (resumeId) {
                    void navigate(`/resumes/preview/${resumeId}`);
                  }
                }}
              >
                <FaFilePdf size={14} className='typst-svg-override me-2' />
                View/Export PDF
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() => {
                  void handleDelete();
                }}
                className='text-danger'
              >
                <FaTrash size={14} className='typst-svg-override me-2' />
                Delete Resume
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </Stack>

      {loading && (
        <div className='d-flex alignments-center'>
          <Spinner animation='border' size='sm' className='me-2' />
          <span>Loading...</span>
        </div>
      )}
      {error && <Alert variant='danger'>{error}</Alert>}

      {!loading && !error && model && (
        <>
          {showPreview && !isMobile ? (
            <ResizableSplit initialLeftPercent={55}>
              <div
                key='left'
                className='mb-3'
                style={{
                  paddingRight: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'calc(100vh - 220px)',
                  overflow: 'auto',
                }}
              >
                <ResumeForm
                  value={model}
                  onChange={v => {
                    setModel(v);
                  }}
                  onSubmit={v => {
                    void handleSubmit(v);
                  }}
                  readOnly={false}
                  aiShowInline={true}
                  aiRewrite={rewriteAndApplyAtPath}
                  aiBusyPath={aiBusy ?? undefined}
                  aiError={aiError ?? undefined}
                />
              </div>

              <div
                key='right'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'calc(100vh - 220px)',
                }}
              >
                <h5 className='mb-3'>Preview</h5>
                <div
                  style={{
                    borderLeft: '1px solid #e9ecef',
                    paddingLeft: '1rem',
                    overflow: 'auto',
                    flex: 1,
                  }}
                >
                  <ResumePreview
                    model={debouncedModel ?? model}
                    defaultFitIfOverflow
                  />
                </div>
              </div>
            </ResizableSplit>
          ) : (
            <>
              <ResumeForm
                value={model}
                onChange={v => {
                  setModel(v);
                }}
                onSubmit={v => {
                  void handleSubmit(v);
                }}
                readOnly={false}
                aiShowInline={true}
                aiRewrite={rewriteAndApplyAtPath}
                aiBusyPath={aiBusy ?? undefined}
                aiError={aiError ?? undefined}
              />
            </>
          )}
        </>
      )}

      {saving && (
        <div className='mt-2'>
          <Spinner animation='grow' size='sm' className='me-2' />
          <span>Saving...</span>
        </div>
      )}

      <Modal
        show={showTailorModal}
        onHide={() => {
          setShowTailorModal(false);
          setTailoredModel(undefined);
          setCreatedJob(undefined);
          setSelectedPaths([]);
          setJobText('');
          setTailorError(undefined);
        }}
        size='xl'
        dialogClassName='tailor-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <span className='d-inline-flex align-items-center'>
              <VscSparkleFilled
                size={20}
                className='typst-svg-override-lg me-2 flex-shrink-0'
              />
              AI Tailor to Job
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {tailorError && <Alert variant='danger'>{tailorError}</Alert>}
          {tailoring && !tailoredModel && (
            <div className='d-flex align-items-center mb-2'>
              <Spinner animation='border' size='sm' className='me-2' />
              <span>Tailoring resume...</span>
            </div>
          )}

          <Form className='mb-3'>
            <Row className='g-2'>
              <Col md={6}>
                <Form.Label className='me-3'>Job Source</Form.Label>
                <div className='d-flex align-items-center' style={{gap: 12}}>
                  <Form.Check
                    type='radio'
                    inline
                    label='Saved'
                    name='job-source'
                    id='job-source-saved'
                    checked={jobMode === 'saved'}
                    onChange={() => {
                      setJobMode('saved');
                    }}
                  />
                  <Form.Check
                    type='radio'
                    inline
                    label='Paste'
                    name='job-source'
                    id='job-source-paste'
                    checked={jobMode === 'paste'}
                    onChange={() => {
                      setJobMode('paste');
                    }}
                  />
                </div>
              </Col>
              <Col md={6}>
                <Form.Group controlId='tailor-strictness'>
                  <Form.Label>Strictness</Form.Label>
                  <Form.Select
                    value={strictness}
                    onChange={event => {
                      const {value} = event.currentTarget;
                      if (
                        value === 'strict' ||
                        value === 'moderate' ||
                        value === 'loose'
                      ) {
                        setStrictness(value);
                      }
                    }}
                  >
                    <option value='strict'>strict</option>
                    <option value='moderate'>moderate</option>
                    <option value='loose'>loose</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {jobMode === 'saved' ? (
              <Form.Group className='mt-2' controlId='tailor-job-select'>
                <Form.Label>Choose Job</Form.Label>
                <div className='d-flex' style={{gap: 8}}>
                  <Form.Select
                    value={selectedJobId}
                    onChange={event => {
                      setSelectedJobId(event.currentTarget.value);
                    }}
                    disabled={jobsLoading}
                  >
                    <option value=''>Select a job…</option>
                    {jobs.map(j => (
                      <option key={j.id} value={j.id}>
                        {j.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    variant='outline-secondary'
                    onClick={() => {
                      (async () => {
                        try {
                          setJobsLoading(true);
                          setJobsError(undefined);
                          const r = await axios.get<JobDescriptionListInfo[]>(
                            '/api/jobs',
                            {
                              params: {per_page: 100},
                            },
                          );
                          setJobs(r.data ?? []);
                        } catch (error_: unknown) {
                          setJobsError(
                            getErrorMessage(error_, 'Failed to load jobs'),
                          );
                        } finally {
                          setJobsLoading(false);
                        }
                      })();
                    }}
                    disabled={jobsLoading}
                  >
                    {jobsLoading && (
                      <Spinner animation='border' size='sm' className='me-1' />
                    )}
                    {jobsLoading ? 'Loading...' : 'Refresh'}
                  </Button>
                </div>
                {jobsLoading && (
                  <div className='text-muted small mt-1 d-flex align-items-center'>
                    <Spinner animation='border' size='sm' className='me-2' />
                    Loading jobs…
                  </div>
                )}
                {jobsError && (
                  <div className='text-danger small mt-1'>{jobsError}</div>
                )}
              </Form.Group>
            ) : (
              <Form.Group className='mt-2' controlId='tailor-job-text'>
                <Form.Label>Paste Job Description</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={6}
                  value={jobText}
                  onChange={event => {
                    setJobText(event.currentTarget.value);
                  }}
                  placeholder='Paste a job description or posting text…'
                />
              </Form.Group>
            )}

            <div className='mt-3'>
              <Button
                disabled={tailoring}
                onClick={() => {
                  void runTailor();
                }}
              >
                {tailoring && (
                  <Spinner animation='border' size='sm' className='me-2' />
                )}
                {tailoring ? 'Tailoring...' : 'Run Tailor'}
              </Button>
            </div>
          </Form>

          {tailoredModel ? (
            <>
              <div className='mb-2'>
                <strong>Preview</strong>
              </div>
              <ResizableSplit initialLeftPercent={50}>
                <div>
                  <h6>Current</h6>
                  <ResumePreview model={model!} defaultFitIfOverflow />
                </div>
                <div>
                  <h6>Tailored</h6>
                  <ResumePreview model={tailoredModel} defaultFitIfOverflow />
                </div>
              </ResizableSplit>

              <div className='mt-3'>
                <h6>Changes</h6>
                <div
                  className='d-flex align-items-center mb-2'
                  style={{gap: 8}}
                >
                  <Button
                    size='sm'
                    variant='outline-secondary'
                    onClick={() => {
                      toggleSelectAll(true);
                    }}
                    disabled={tailorChanges.length === 0}
                  >
                    Select all
                  </Button>
                  <Button
                    size='sm'
                    variant='outline-secondary'
                    onClick={() => {
                      toggleSelectAll(false);
                    }}
                    disabled={selectedPaths.length === 0}
                  >
                    Clear
                  </Button>
                </div>
                <div
                  style={{
                    maxHeight: '30vh',
                    overflowY: 'auto',
                    border: '1px solid #eee',
                    padding: 8,
                    borderRadius: 4,
                  }}
                >
                  {tailorChanges.length === 0 ? (
                    <div className='text-muted'>No differences detected.</div>
                  ) : (
                    <div>
                      {tailorChanges.map((c, i) => {
                        const key = pathKey(c.path);
                        const checked = selectedPaths.includes(key);
                        const beforeS =
                          typeof c.before === 'string'
                            ? c.before
                            : JSON.stringify(c.before);
                        const afterS =
                          typeof c.after === 'string'
                            ? c.after
                            : JSON.stringify(c.after);
                        const trunc = (s: string) =>
                          s.length > 140 ? s.slice(0, 140) + '…' : s;
                        return (
                          <div key={key || i} className='mb-2'>
                            <Form.Check
                              type='checkbox'
                              id={`chg-${i}`}
                              label={
                                <div>
                                  <code>{prettyPath(c.path)}</code>{' '}
                                  <span className='badge bg-light text-dark'>
                                    {c.kind}
                                  </span>
                                  <div className='small text-muted'>
                                    {trunc(beforeS || '')} →{' '}
                                    {trunc(afterS || '')}
                                  </div>
                                </div>
                              }
                              checked={checked}
                              onChange={event => {
                                const v = event.currentTarget.checked;
                                setSelectedPaths(previous =>
                                  v
                                    ? [...previous, key].filter(
                                        (value, index, array) =>
                                          array.indexOf(value) === index,
                                      )
                                    : previous.filter(k => k !== key),
                                );
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => {
              setShowTailorModal(false);
              setTailoredModel(undefined);
              setCreatedJob(undefined);
              setSelectedPaths([]);
              setJobText('');
              setTailorError(undefined);
            }}
            disabled={tailorSaving || tailoring}
          >
            Close
          </Button>
          <Button
            variant='outline-secondary'
            onClick={() => {
              void saveAsNew();
            }}
            disabled={!tailoredModel || tailorSaving}
          >
            {tailorSaving && (
              <Spinner animation='border' size='sm' className='me-1' />
            )}
            {tailorSaving ? 'Saving...' : 'Save as new'}
          </Button>
          <Button
            variant='outline-primary'
            onClick={() => {
              void applySelected();
            }}
            disabled={
              !tailoredModel || selectedPaths.length === 0 || tailorSaving
            }
          >
            {tailorSaving && (
              <Spinner animation='border' size='sm' className='me-1' />
            )}
            {tailorSaving ? 'Saving...' : 'Apply selected'}
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              void acceptAllReplace();
            }}
            disabled={!tailoredModel || tailorSaving}
          >
            {tailorSaving && (
              <Spinner animation='border' size='sm' className='me-1' />
            )}
            {tailorSaving ? 'Saving...' : 'Accept all (replace)'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Offcanvas
        placement='end'
        show={showAssistant}
        onHide={() => {
          setShowAssistant(false);
        }}
        scroll
        backdrop={false}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <span className='d-inline-flex align-items-center'>
              <VscSparkleFilled
                size={20}
                className='typst-svg-override-lg me-2 flex-shrink-0'
              />
              AI Assistant
            </span>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Tabs
            activeKey={assistantTab}
            onSelect={k => {
              const nextTab = k === 'ats' || k === 'rewrite' ? k : 'critique';
              setAssistantTab(nextTab);
            }}
            className='mb-3'
          >
            <Tab eventKey='critique' title='Critique'>
              {critError && (
                <Alert variant='danger' className='mt-2'>
                  {critError}
                </Alert>
              )}

              <Form className='mb-2'>
                <Row className='g-2'>
                  <Col md={6}>
                    <Form.Group controlId='assistant-crit-target-title'>
                      <Form.Label>Target job title (optional)</Form.Label>
                      <Form.Control
                        type='text'
                        value={critTargetJobTitle}
                        onChange={event => {
                          setCritTargetJobTitle(event.currentTarget.value);
                        }}
                        placeholder='e.g., Senior Frontend Engineer'
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId='assistant-crit-target-industry'>
                      <Form.Label>Target industry (optional)</Form.Label>
                      <Form.Control
                        type='text'
                        value={critTargetIndustry}
                        onChange={event => {
                          setCritTargetIndustry(event.currentTarget.value);
                        }}
                        placeholder='e.g., FinTech'
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className='mt-2' controlId='assistant-crit-extra'>
                  <Form.Label>Extra instructions (optional)</Form.Label>
                  <Form.Control
                    as='textarea'
                    rows={2}
                    value={critExtraInstructions}
                    onChange={event => {
                      setCritExtraInstructions(event.currentTarget.value);
                    }}
                    placeholder='Any guidance for the critique (tone, focus areas, etc.)'
                  />
                </Form.Group>
              </Form>

              <div className='d-flex align-items-center my-2' style={{gap: 8}}>
                <Button
                  size='sm'
                  onClick={() => {
                    void runEditorCritique();
                  }}
                  disabled={critLoading || !model}
                  className='icon-btn'
                >
                  {critLoading ? (
                    <>
                      <Spinner animation='border' size='sm' className='me-1' />
                      Running...
                    </>
                  ) : (
                    <>
                      <VscSparkleFilled
                        size={16}
                        className='typst-svg-override me-1 flex-shrink-0'
                      />
                      Run Critique
                    </>
                  )}
                </Button>
                <Button
                  size='sm'
                  variant='primary'
                  onClick={() => {
                    void saveEditorChanges();
                  }}
                  disabled={!model || !resumeId}
                >
                  Save changes
                </Button>
              </div>
              {critLoading && (
                <div className='d-flex align-items-center my-2'>
                  <Spinner animation='border' size='sm' className='me-2' />
                  <span>Running critique...</span>
                </div>
              )}
              <BasicAiAlert />
              {!critique && !critLoading ? (
                <div className='text-muted small mt-2'>
                  Run a critique to see suggested changes and actions.
                </div>
              ) : null}

              {critique ? (
                <div style={{maxHeight: '70vh', overflowY: 'auto'}}>
                  {critique.prioritized_actions?.length ? (
                    <>
                      <h6 className='mt-3'>Prioritized actions</h6>
                      <ol>
                        {critique.prioritized_actions.map((a, i) => (
                          <li key={`pa-${i}`}>
                            <strong>P{a.priority}:</strong> {a.description}{' '}
                            <span className='text-muted'>
                              ({a.estimated_time}, {a.impact})
                            </span>
                          </li>
                        ))}
                      </ol>
                    </>
                  ) : null}

                  {critique.exact_rewrites?.length ? (
                    <>
                      <h6 className='mt-3'>Exact rewrites</h6>
                      {critique.exact_rewrites.map((rw, i) => (
                        <div key={`rw-${i}`} className='mb-2'>
                          <div>
                            <code>{rw.location}</code>
                          </div>
                          <div className='small text-muted'>
                            '{rw.original_text}' → '{rw.replacement_text}'
                          </div>
                          <Button
                            size='sm'
                            variant='outline-primary'
                            className='mt-1'
                            onClick={() => {
                              applyExactRewrite(rw);
                            }}
                          >
                            Apply
                          </Button>
                        </div>
                      ))}
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        onClick={applyAllExactRewrites}
                      >
                        Apply all
                      </Button>
                    </>
                  ) : null}

                  <>
                    <h6 className='mt-3'>Sample bullets</h6>
                    {model?.resume?.work?.length ? (
                      <Form.Select
                        size='sm'
                        style={{width: '100%', maxWidth: 360}}
                        className='mb-2'
                        value={targetRoleIdx}
                        onChange={event => {
                          setTargetRoleIdx(Number(event.currentTarget.value));
                        }}
                      >
                        {(model?.resume?.work ?? []).map((w, i: number) => (
                          <option key={`role-${i}`} value={i}>
                            {w?.position ?? w?.name ?? `Role #${i + 1}`}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <div className='text-muted small'>
                        No work roles found to insert bullets.
                      </div>
                    )}
                    {critique.sample_bullets?.map((sb, i) => (
                      <div key={`sb-${i}`} className='mb-2'>
                        {(sb.role ?? sb.company) ? (
                          <div className='small text-muted'>
                            {[sb.role, sb.company].filter(Boolean).join(' — ')}
                          </div>
                        ) : null}
                        <ul style={{marginBottom: 6}}>
                          {sb.bullets.map((b, j) => (
                            <li key={`sbb-${i}-${j}`}>{b}</li>
                          ))}
                        </ul>
                        <Button
                          size='sm'
                          variant='outline-primary'
                          onClick={() => {
                            insertBulletsIntoWork(sb.bullets, targetRoleIdx);
                          }}
                          disabled={!(model?.resume?.work?.length ?? 0)}
                        >
                          Insert into selected role
                        </Button>
                      </div>
                    ))}
                  </>

                  {critique.ats_keyword_gap?.length ? (
                    <>
                      <h6 className='mt-3'>ATS keyword gap</h6>
                      {critique.ats_keyword_gap.map((g, i) => (
                        <div key={`kg-${i}`} className='mb-2'>
                          <div>
                            <strong>{g.keyword}</strong>
                            {g.where_to_add ? (
                              <span className='text-muted'>
                                {' '}
                                — {g.where_to_add}
                              </span>
                            ) : null}
                          </div>
                          <div className='d-flex' style={{gap: 8}}>
                            <Button
                              size='sm'
                              variant='outline-primary'
                              onClick={() => {
                                addKeywordToSkills(g.keyword);
                              }}
                            >
                              Add to Skills
                            </Button>
                            <Button
                              size='sm'
                              variant='outline-secondary'
                              onClick={() => {
                                insertBulletsIntoWork(
                                  [g.keyword],
                                  targetRoleIdx,
                                );
                              }}
                              disabled={!(model?.resume?.work?.length ?? 0)}
                            >
                              Insert into current role highlights
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : null}
                </div>
              ) : null}
            </Tab>
            <Tab eventKey='ats' title='ATS'>
              {critError && (
                <Alert variant='danger' className='mt-2'>
                  {critError}
                </Alert>
              )}
              {critLoading && (
                <div className='d-flex align-items-center my-2'>
                  <Spinner animation='border' size='sm' className='me-2' />
                  <span>Loading critique...</span>
                </div>
              )}
              {!critique && !critLoading ? (
                <Alert variant='info' className='mt-2'>
                  Run a critique first to see ATS keyword suggestions.
                </Alert>
              ) : null}

              {critique && (
                <div>
                  <div
                    className='d-flex align-items-center mb-2'
                    style={{gap: 8}}
                  >
                    <Form.Label className='mb-0'>
                      Target role for insertion
                    </Form.Label>
                    {model?.resume?.work?.length ? (
                      <Form.Select
                        size='sm'
                        style={{width: '100%', maxWidth: 360}}
                        value={targetRoleIdx}
                        onChange={event => {
                          setTargetRoleIdx(Number(event.currentTarget.value));
                        }}
                      >
                        {(model?.resume?.work ?? []).map((w, i: number) => (
                          <option key={`role-${i}`} value={i}>
                            {w?.position ?? w?.name ?? `Role #${i + 1}`}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <span className='text-muted small'>
                        No work roles found.
                      </span>
                    )}
                  </div>

                  {critique.ats_keyword_gap?.length ? (
                    <div style={{maxHeight: '70vh', overflowY: 'auto'}}>
                      {critique.ats_keyword_gap.map((g, i) => {
                        const kw = String(g.keyword || '').trim();
                        const kwLower = kw.toLowerCase();

                        const resume = model?.resume ?? {};
                        const skillsArray = resume.skills ?? [];
                        const hasSkill = skillsArray.some(
                          s => (s?.name ?? '').toLowerCase() === kwLower,
                        );

                        const work = resume.work ?? [];
                        const hasWork = work.length > 0;
                        const role = hasWork ? (work[targetRoleIdx] ?? {}) : {};
                        const highlights = role.highlights ?? [];
                        const bulletText = `• ${kw}`;
                        const hasInRole =
                          highlights.includes(bulletText) ??
                          highlights.includes(kw);

                        const recentlyAdded = recentAts.has(kwLower);

                        const handleAddToSkills = () => {
                          if (hasSkill) {
                            return;
                          }

                          addKeywordToSkills(kw);
                          setRecentAts(previous => {
                            const ns = new Set(previous);
                            ns.add(kwLower);
                            return ns;
                          });
                        };

                        const handleInsertIntoRole = () => {
                          if (!hasWork || hasInRole) {
                            return;
                          }

                          insertBulletsIntoWork([kw], targetRoleIdx);
                          setRecentAts(previous => {
                            const ns = new Set(previous);
                            ns.add(kwLower);
                            return ns;
                          });
                        };

                        return (
                          <div key={`kg-${i}`} className='mb-3'>
                            <div
                              className='d-flex align-items-center'
                              style={{gap: 8}}
                            >
                              <strong>{kw}</strong>
                              {g.where_to_add ? (
                                <span className='text-muted small'>
                                  {' '}
                                  — {g.where_to_add}
                                </span>
                              ) : null}
                              {recentlyAdded ? (
                                <span className='badge bg-success'>Added</span>
                              ) : null}
                            </div>
                            <div
                              className='d-flex align-items-center mt-1'
                              style={{gap: 8, flexWrap: 'wrap'}}
                            >
                              <Button
                                size='sm'
                                variant={
                                  hasSkill
                                    ? 'outline-secondary'
                                    : 'outline-primary'
                                }
                                disabled={hasSkill}
                                onClick={handleAddToSkills}
                              >
                                {hasSkill
                                  ? 'Already in Skills'
                                  : 'Add to Skills'}
                              </Button>
                              <Button
                                size='sm'
                                variant={
                                  (hasInRole ?? !hasWork)
                                    ? 'outline-secondary'
                                    : 'outline-primary'
                                }
                                disabled={hasInRole ?? !hasWork}
                                onClick={handleInsertIntoRole}
                              >
                                {hasInRole
                                  ? 'Already in current role'
                                  : 'Insert into current role highlights'}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className='text-muted small mt-2'>
                      No ATS keyword gaps found in the critique.
                    </div>
                  )}
                </div>
              )}
            </Tab>
            <Tab eventKey='rewrite' title='Rewrite'>
              {rewriteError && (
                <Alert variant='danger' className='mt-2'>
                  {rewriteError}
                </Alert>
              )}

              <Form>
                <Row className='g-2'>
                  <Col md={6}>
                    <Form.Group controlId='rewrite-mode'>
                      <Form.Label>Mode</Form.Label>
                      <Form.Select
                        size='sm'
                        value={rewriteMode}
                        onChange={event => {
                          const {value} = event.currentTarget;
                          if (
                            value === 'quantify' ||
                            value === 'shorten' ||
                            value === 'strengthen' ||
                            value === 'tone_formal' ||
                            value === 'tone_plain' ||
                            value === 'keyword_boost'
                          ) {
                            setRewriteMode(value);
                          }
                        }}
                      >
                        <option value='quantify'>Quantify</option>
                        <option value='shorten'>Shorten</option>
                        <option value='strengthen'>Strengthen</option>
                        <option value='tone_formal'>Tone: Formal</option>
                        <option value='tone_plain'>Tone: Plain-English</option>
                        <option value='keyword_boost'>Keyword boost</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <div className='mt-2'>
                  <div className='fw-semibold mb-1'>Scopes</div>
                  <div className='d-flex flex-wrap' style={{gap: 12}}>
                    <Form.Check
                      type='checkbox'
                      id='scope-basics-summary'
                      label='Basics summary'
                      checked={Boolean(rewriteScopes.basics_summary)}
                      onChange={event => {
                        setRewriteScopes(previous => ({
                          ...previous,
                          basics_summary: event.currentTarget.checked,
                        }));
                      }}
                    />
                    <Form.Check
                      type='checkbox'
                      id='scope-work-descriptions'
                      label='Work descriptions'
                      checked={Boolean(rewriteScopes.work_descriptions)}
                      onChange={event => {
                        setRewriteScopes(previous => ({
                          ...previous,
                          work_descriptions: event.currentTarget.checked,
                        }));
                      }}
                    />
                    <Form.Check
                      type='checkbox'
                      id='scope-work-summaries'
                      label='Work summaries'
                      checked={Boolean(rewriteScopes.work_summaries)}
                      onChange={event => {
                        setRewriteScopes(previous => ({
                          ...previous,
                          work_summaries: event.currentTarget.checked,
                        }));
                      }}
                    />
                    <Form.Check
                      type='checkbox'
                      id='scope-work-highlights'
                      label='Work highlights'
                      checked={Boolean(rewriteScopes.work_highlights)}
                      onChange={event => {
                        setRewriteScopes(previous => ({
                          ...previous,
                          work_highlights: event.currentTarget.checked,
                        }));
                      }}
                    />
                    <Form.Check
                      type='checkbox'
                      id='scope-project-descriptions'
                      label='Project descriptions'
                      checked={Boolean(rewriteScopes.project_descriptions)}
                      onChange={event => {
                        setRewriteScopes(previous => ({
                          ...previous,
                          project_descriptions: event.currentTarget.checked,
                        }));
                      }}
                    />
                    <Form.Check
                      type='checkbox'
                      id='scope-project-highlights'
                      label='Project highlights'
                      checked={Boolean(rewriteScopes.project_highlights)}
                      onChange={event => {
                        setRewriteScopes(previous => ({
                          ...previous,
                          project_highlights: event.currentTarget.checked,
                        }));
                      }}
                    />
                    <Form.Check
                      type='checkbox'
                      id='scope-volunteer-summaries'
                      label='Volunteer summaries'
                      checked={Boolean(rewriteScopes.volunteer_summaries)}
                      onChange={event => {
                        setRewriteScopes(previous => ({
                          ...previous,
                          volunteer_summaries: event.currentTarget.checked,
                        }));
                      }}
                    />
                    <Form.Check
                      type='checkbox'
                      id='scope-volunteer-highlights'
                      label='Volunteer highlights'
                      checked={Boolean(rewriteScopes.volunteer_highlights)}
                      onChange={event => {
                        setRewriteScopes(previous => ({
                          ...previous,
                          volunteer_highlights: event.currentTarget.checked,
                        }));
                      }}
                    />
                  </div>
                  <div className='text-muted small mt-1'>
                    Note: Fields longer than 2000 characters are skipped to
                    satisfy API limits.
                  </div>
                </div>

                <div
                  className='d-flex align-items-center mt-3'
                  style={{gap: 8, flexWrap: 'wrap'}}
                >
                  <Button
                    size='sm'
                    onClick={() => {
                      void runBulkRewrite();
                    }}
                    disabled={rewriteRunning || !model}
                    className='icon-btn'
                  >
                    {rewriteRunning ? (
                      <>
                        <Spinner
                          animation='border'
                          size='sm'
                          className='me-1'
                        />
                        Rewriting...
                      </>
                    ) : (
                      'Run bulk rewrite'
                    )}
                  </Button>
                  <Button
                    size='sm'
                    variant='primary'
                    onClick={() => {
                      void saveEditorChanges();
                    }}
                    disabled={rewriteRunning || !model || !resumeId}
                  >
                    Save changes
                  </Button>

                  {(rewriteRunning || rewriteProgress.total > 0) && (
                    <div className='ms-auto small text-muted'>
                      Progress: {rewriteProgress.done}/{rewriteProgress.total}
                      {rewriteSkipped > 0
                        ? ` — Skipped: ${rewriteSkipped}`
                        : ''}
                    </div>
                  )}
                </div>

                <hr className='my-3' />

                <div>
                  <div className='fw-semibold mb-2'>Rewrite using critique</div>
                  <div
                    className='d-flex align-items-center'
                    style={{gap: 8, flexWrap: 'wrap'}}
                  >
                    <Button
                      size='sm'
                      variant='outline-primary'
                      onClick={() => {
                        void rewriteUsingCritique();
                      }}
                      disabled={rewriteRunning || !model}
                    >
                      Generate rewritten draft
                    </Button>
                    {!critique && (
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        onClick={() => {
                          void runEditorCritique();
                        }}
                        disabled={critLoading || rewriteRunning || !model}
                      >
                        {critLoading
                          ? 'Running critique...'
                          : 'Run critique first'}
                      </Button>
                    )}
                  </div>
                  <div className='text-muted small mt-1'>
                    This generates a full rewritten draft using your critique
                    and opens the Tailor dialog to review and apply changes.
                  </div>
                </div>
              </Form>
            </Tab>
          </Tabs>
        </Offcanvas.Body>
      </Offcanvas>

      <style>{`
        .modal-dialog.tailor-modal {
          --bs-modal-width: 90vw;
        }
      `}</style>
    </div>
  );
}

function ResumeCreate(): React.JSX.Element {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [model, setModel] = React.useState<ResumeDbModel>({
    name: '',
    id: '',
    user_id: '',
    resume: {},
  });
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [saving, setSaving] = React.useState(false);

  const handleSubmit = async (v: ResumeDbModel) => {
    const validationErrors = validateResumePayload(v);
    if (validationErrors.summary.length > 0) {
      setError(
        `Fix these resume format issues: ${validationErrors.summary.join(' ')}`,
      );
      return;
    }

    try {
      setSaving(true);
      setError(undefined);
      const response = await axios.post<ResumeDbModel>('/api/resumes', v);
      const created = response.data;
      try {
        dispatch(setHasResume(true));
      } catch {}

      if (typeof created?.id === 'string') {
        void navigate(`/resumes/${created.id}`);
      } else {
        void navigate('/resumes');
      }
    } catch (error: unknown) {
      setError(`Failed to create: ${getErrorMessage(error, 'Unknown error')}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container className='my-5'>
      <Stack direction='horizontal' className='mb-3' gap={2}>
        <h2 className='mb-0'>Create Resume</h2>
        <div className='ms-auto'>
          <Button
            variant='outline-secondary'
            onClick={() => {
              void navigate('/resumes');
            }}
          >
            Back
          </Button>
        </div>
      </Stack>
      {error && <Alert variant='danger'>{error}</Alert>}
      <ResumeForm
        value={model}
        onChange={v => {
          setModel(v);
        }}
        onSubmit={v => {
          void handleSubmit(v);
        }}
        readOnly={false}
      />
      {saving && (
        <div className='mt-2'>
          <Spinner animation='grow' size='sm' className='me-2' />
          <span>Creating...</span>
        </div>
      )}
    </Container>
  );
}

function AiResumeBuilder(): React.JSX.Element {
  const [input, setInput] = React.useState<string>('');
  const [result, setResult] = React.useState<ResumeDbModel | undefined>(
    undefined,
  );
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError(undefined);
      const response = await axios.post<ResumeDbModel>('/api/ai/resumes', {
        resume_text: input.normalize('NFC'),
      });
      setResult(response.data);
      try {
        dispatch(setHasTailored(true));
      } catch {}
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to generate AI resume'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (v: ResumeDbModel) => {
    const validationErrors = validateResumePayload(v);
    if (validationErrors.summary.length > 0) {
      setError(
        `Fix these resume format issues: ${validationErrors.summary.join(' ')}`,
      );
      return;
    }

    try {
      setLoading(true);
      setError(undefined);
      const response = await axios.post<ResumeDbModel>('/api/resumes', v);
      const created = response.data;
      try {
        dispatch(setHasResume(true));
      } catch {}

      if (typeof created?.id === 'string') {
        void navigate(`/resumes/${created.id}`);
      } else {
        void navigate('/resumes');
      }
    } catch (error_: unknown) {
      setError(`Failed to save: ${getErrorMessage(error_, 'Unknown error')}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = () => {
    setResult(undefined);
    setInput('');
    void navigate('/resumes');
  };

  return (
    <Container className='my-5'>
      <Stack direction='horizontal' className='mb-3' gap={2}>
        <h2 className='mb-0'>AI Resume Builder</h2>
        <div className='ms-auto'>
          <Button
            variant='outline-secondary'
            onClick={() => {
              void navigate('/resumes');
            }}
          >
            Back
          </Button>
        </div>
      </Stack>

      {!result && (
        <>
          {error && <Alert variant='danger'>{error}</Alert>}
          <Form>
            <Form.Group className='mb-3' controlId='aiResumeText'>
              <Form.Label>
                Paste resume text (or paste your plain resume)
              </Form.Label>
              <Form.Control
                as='textarea'
                rows={8}
                value={input}
                onChange={event => {
                  setInput(event.currentTarget.value);
                }}
                placeholder='Paste resume content here...'
              />
            </Form.Group>
            <BasicAiAlert />
            <div className='d-flex alignments-center'>
              <Button
                disabled={!input || loading}
                onClick={() => {
                  void handleGenerate();
                }}
              >
                {loading ? (
                  'Generating...'
                ) : (
                  <span className='d-inline-flex align-items-center'>
                    <VscSparkleFilled
                      size={16}
                      className='typst-svg-override me-1'
                    />{' '}
                    Generate AI Resume
                  </span>
                )}
              </Button>
              {loading && (
                <Spinner animation='border' size='sm' className='ms-2' />
              )}
            </div>
          </Form>
        </>
      )}

      {result && (
        <>
          <Alert variant='info'>
            AI resume generated — you can edit the result below. Save to persist
            or discard to cancel.
          </Alert>
          <ResumeForm
            value={result}
            onChange={v => {
              setResult(v);
            }}
            onSubmit={v => {
              void handleSave(v);
            }}
            readOnly={false}
            showActions={true}
            showTopActions={true}
          />
          <div className='d-flex justify-content-end mt-2'>
            <Button
              variant='outline-secondary'
              className='me-2'
              onClick={handleDiscard}
              disabled={loading}
            >
              Discard
            </Button>
          </div>
        </>
      )}
    </Container>
  );
}

function ResumesPage(): React.JSX.Element {
  return (
    <div>
      <Routes>
        <Route index element={<ResumesList />} />
        <Route path='new' element={<ResumeCreate />} />
        <Route path='ai' element={<AiResumeBuilder />} />
        <Route path='import-pdf' element={<ImportPdfPage />} />
        <Route path=':id' element={<ResumeEditor />} />
        <Route path='preview/:id' element={<ResumePreviewWrapper />} />
      </Routes>
    </div>
  );
}

export default ResumesPage;
